import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import { REDUCE_MOTION, applyAppTheme, getAppTheme, previewAppTheme, applyFont, getAppFont, previewFont } from "./lib/theme";
import { Icon, TI } from "./icons";
import { BuzerIcon, ICON_NAMES, ICON_CATEGORIES, CATEGORY_LABELS, ICON_LABEL } from "./BuzerIcons";
import { MOOD_ICON } from "./MoodIcons";
import { api, saveOrder } from "./lib/api";
import { st } from "./styles.js";
import { session, setSession } from "./lib/session";
import { LEVEL_CATS, LEVELS, levelIndex, getLevel, nextLevel, gname } from "./lib/levels";
import { BADGE_ICON } from "./BadgeIcons";   // v20 §23 — badge code → BuzerIcon name
import { LEVEL_ICON, TIER_ICON } from "./LevelIcons";    // v20 §25 — level/tier → BuzerIcon name
import { todayKey, fmtShortDate, pad2, ymd, WEEKDAYS_SK, OFFSETS, offLabel, relTime } from "./lib/format";
import { usePoll } from "./hooks/usePoll";
import { useIsNarrow } from "./hooks/useIsNarrow";
import { resolveIcon } from "./lib/icons-resolve";
import { fmtDuePretty, fmtDayLong } from "./lib/date-sk";
import { MOODS, moodIconName, SUGGEST_ICONS, DUR_PRESETS, DUR_OPTIONS, IS_TOUCH, APP_THEME_SWATCHES, SWATCH, QUOTES, REACTIONS, EFFECTS } from "./lib/constants";
import { useScrollLock, useLongPress, hasOpenModal, closeTopModal, useModalBackClose, Avatar, BottomNav, Glyph, DueLabel, LevelUpModal, Modal, FormShell, cleanTitle, Brand, ProUpsell, ProButton, Chk, HintBanner, MoodFace, Rank, OvStat, FPill, ProfStat, EffectBurst, IconPicker, Timer, BadgeGrid, InstallGuide, ProfileModalProvider } from "./components/ui";
import { Sheet, Button, IconButton, SectionHeader, Tag, Typewriter } from "./components/ui/ds";
import { ChallengeBoard, ChallengeCreate, CompetitionDetail } from "./features/challenges/challenges";
import { AddCard } from "./components/forms/AddCard";
import { QuickPlan } from "./components/forms/QuickPlan";
import { PlanTab } from "./features/plan/PlanTab";
import { remindersForMonth } from "./lib/calendar";
import { focusEl } from "./lib/focus";  // v26.2 §1 — center + flash a notification's subject
import { isStandalone, installApp, enablePush, ensurePushSubscribed, clearNotifications } from "./lib/pwa";
import { SocialTab, PublicProfile, PokeModal, BuzerakSheet } from "./features/social/SocialFeature";
import { ProfilHub, ShopSheet } from "./features/profile/ProfileFeature";
import { BuzerTab } from "./features/buzer/BuzerFeature";
import "./components/ui/speeddial.css";
import "./components/ui/notifdial.css";
import "./components/ui/moredial.css";


// ── config ──
// 12 categories (tiers) of 5 levels each → 60 levels. Novice (playful) → Super Godlike.






// Poll `fn` every `ms` while `active` (clears on unmount/inactive). Used by chat.

// ─────────────────────────────────────────────────────────────
//  Prihlásenie / registrácia
// ─────────────────────────────────────────────────────────────
function Auth({ onAuthed, allowReg }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [gender, setGender] = useState("z");
  const [showPw, setShowPw] = useState(false);
  const [hp, setHp] = useState("");  // honeypot — must stay empty (bots fill it)
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const reg = mode === "register";
  async function submit(e) {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api(reg ? "/api/register" : "/api/login", {
        method: "POST",
        body: reg ? { email, password: pw, hp } : { email, password: pw },
      });
      onAuthed();
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <div style={st.page}>
      <div style={{ ...st.container, maxWidth: 380, paddingTop: 40 }}>
        <header style={st.header}>
          <div style={{ marginTop: 6 }}><Brand size={34} /></div>
          <p style={{ ...st.quote, marginTop: 10 }}>„Malé kroky každý deň."</p>
        </header>
        <form onSubmit={submit} style={st.card}>
          <div style={st.seg}>
            <button type="button" onClick={() => setMode("login")} className="seg-btn"
              style={{ ...st.segBtn, ...(reg ? {} : st.segOn) }}>Prihlásiť</button>
            {allowReg && (
              <button type="button" onClick={() => setMode("register")} className="seg-btn"
                style={{ ...st.segBtn, ...(reg ? st.segOn : {}) }}>Registrácia</button>
            )}
          </div>
          {reg && <div style={{ ...st.taskDesc, marginBottom: 2, display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="sprout" size={14} color="var(--accent)" /> Po registrácii ťa krátko prevedieme nastavením.</div>}
          <input className="field" style={st.field} type="email" placeholder="E-mail"
            value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
          <div style={{ position: "relative", display: "flex" }}>
            <input className="field" style={{ ...st.field, flex: 1, paddingRight: 44 }}
              type={showPw ? "text" : "password"} placeholder="Heslo"
              value={pw} onChange={(e) => setPw(e.target.value)}
              autoComplete={reg ? "new-password" : "current-password"} required />
            <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
              title={showPw ? "Skryť heslo" : "Zobraziť heslo"} aria-label="Zobraziť heslo"
              style={{ position: "absolute", right: 6, top: 0, bottom: 0, background: "transparent", border: "none", cursor: "pointer", padding: "0 8px", color: "var(--muted)", display: "flex", alignItems: "center" }}>
              <BuzerIcon name={showPw ? "eyeoff" : "eye"} size={19} color="currentColor" />
            </button>
          </div>
          {/* honeypot — hidden from humans; bots that fill it are silently rejected */}
          <input value={hp} onChange={(e) => setHp(e.target.value)} name="website" tabIndex={-1}
            autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
          {err && <div style={st.err}>{err}</div>}
          <button type="submit" disabled={busy} style={{ ...st.primaryBtn, background: "var(--ink)", color: "var(--surface)" }}>
            {busy ? "…" : reg ? "Vytvoriť účet" : "Prihlásiť sa"}
          </button>
          {/* v27 — právne dokumenty: informovaný súhlas pri registrácii */}
          {reg && (
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 10, lineHeight: 1.5 }}>
              Vytvorením účtu súhlasíš s <a href="/obchodne-podmienky" target="_blank" rel="noopener" style={{ color: "inherit" }}>Obchodnými podmienkami</a> a
              potvrdzuješ, že si sa oboznámil/a so <a href="/ochrana-osobnych-udajov" target="_blank" rel="noopener" style={{ color: "inherit" }}>Zásadami ochrany osobných údajov</a>.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Pridávacie formuláre (jeden pre každý druh)
// ─────────────────────────────────────────────────────────────
// `task` set → edit mode (PATCH existing); otherwise create mode (POST new).
// G1 — small inline check icon (replaces the bare "✓" glyph)
















// Co-create a shared (co-owned) event: pick a friend, then fill the event form.




// F8 — compact friend/user profile popup (read-only stats + contextual actions)
// PR2 — profile stat with a Lucide icon (matches the rest of the app)







// ─────────────────────────────────────────────────────────────
//  Verejný profil — séria, súťaže, status + reakcie, fotka
// ─────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────
//  TAB: Social (priatelia + chat na jednom mieste)
// ─────────────────────────────────────────────────────────────
// F5 — "report a bug/idea" now opens from the + sheet as a popup form
function ContactModal({ onClose }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  async function send(e) {
    e.preventDefault(); setErr("");
    if (msg.trim().length < 3) { setErr("Napíš správu."); return; }
    setBusy(true);
    try { await api("/api/contact", { method: "POST", body: { message: msg } }); setDone(true); setMsg(""); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }
  return (
    <Modal center title="Nahlásiť chybu alebo návrh" icon="mail" onClose={onClose} maxWidth={480}>
      {done ? (
        <div style={{ textAlign: "center" }}>
          <div style={{ ...st.infoNote, marginBottom: 14 }}><Icon name="check" size={16} color="var(--success)" stroke={3} style={{ verticalAlign: "-3px", marginRight: 6 }} />Odoslané, ďakujeme.</div>
          <button type="button" onClick={onClose} style={{ ...st.primaryBtn, width: "100%" }}>Zavrieť</button>
        </div>
      ) : (
        <form onSubmit={send}>
          <div style={st.fieldHint}>Napíš adminovi — chyba, návrh alebo otázka (nie cez chat).</div>
          <textarea className="field" style={{ ...st.field, minHeight: 110, resize: "vertical", width: "100%" }} maxLength={2000}
            value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Tvoja správa…" autoFocus />
          {err && <div style={st.err}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" onClick={onClose} style={st.cancelBtn}>Zrušiť</button>
            <button type="submit" disabled={busy} style={{ ...st.primaryBtn, flex: 1 }}>{busy ? "…" : "Odoslať"}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}






// v23 — "Rozpracované s AI" / deferred intents feature removed (replaced by „Ulož ako poznámku").


// ─────────────────────────────────────────────────────────────
//  Spodný panel „Čo chceš vytvoriť?" (globálny + button)
// ─────────────────────────────────────────────────────────────
// v13 A7 — on-device voice dictation (Web Speech API). Feature-detected; renders nothing where
// unsupported (e.g. Firefox / non-installed iOS Safari). Appends the recognised text via onText.
const _SpeechRec = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
function MicButton({ onText, disabled, size = 46, round = false }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  if (!_SpeechRec) return null;
  function toggle() {
    if (listening) { try { recRef.current && recRef.current.stop(); } catch (e) { /* */ } setListening(false); return; }
    let rec;
    try { rec = new _SpeechRec(); } catch (e) { return; }
    rec.lang = "sk-SK"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => { const t = e.results && e.results[0] && e.results[0][0] && e.results[0][0].transcript; if (t) onText(t.trim()); };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec; setListening(true);
    try { rec.start(); } catch (e) { setListening(false); }
  }
  const roundSt = round
    ? { width: size, height: size, borderRadius: "50%", border: "1px solid var(--border)",
        background: listening ? "var(--accent)" : "var(--surface)", color: listening ? "var(--on-ember)" : "var(--text2)" }
    : { ...st.field, width: size, background: listening ? "var(--accent)" : "var(--ember-soft)", borderColor: "var(--accent)", color: listening ? "var(--on-ember)" : "var(--accent)" };
  return (
    <button type="button" onClick={toggle} disabled={disabled} title="Diktovať hlasom" aria-label="Diktovať hlasom"
      style={{ ...roundSt, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
      <Icon name="mic" size={18} color="currentColor" />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// v12 — AI command bar: free text → backend interprets + executes (auto/confirm/defer), with undo.
// v12 — open the edit modal for a task the AI command just created. Looks the task up in the
// freshly-reloaded state and renders the matching editor (AddCard for habits, PlanCreate for plan).
function QuickEditModal({ refData, s, reload, onClose }) {
  const { kind, id } = refData || {};
  let task = null;
  if (kind === "habit") task = (s.habits || []).find((h) => h.id === id);
  else if (kind === "onetime") { const t = (s.onetime || []).find((x) => x.id === id); if (t) task = { ...t, _kind: "onetime" }; }
  else if (kind === "reminder") { const t = (s.reminders || []).find((x) => x.id === id); if (t) task = { ...t, _kind: "reminder" }; }
  useEffect(() => { if (!task) onClose(); }, [!task]);  // vanished (e.g. undone) → just close
  if (!task) return null;
  if (kind === "habit")  // AddCard self-wraps as a modal when editing
    return <AddCard kind="habit" task={task} onAdded={reload} onCancel={onClose} routines={s.categories || []}  lite={!!s.lite_mode} />;
  // AddCard(kind=reminder, task) self-wraps as the Pripomienka sheet when editing
  const planCategories = [...new Set((s.onetime || []).map((t) => t.category).filter(Boolean))];
  return <AddCard kind="reminder" task={task} onAdded={reload} onCancel={onClose} categories={planCategories} />;
}

// v16 §11 — suggested openers (mock image #4)
const CHAT_CHIPS = ["návyk piť 2 litre vody", "výzva behať s rodinou", "Navrhni mi večernú rutinu", "Mám stres a zle spím"];
function _chatGroup(updatedAt) {
  const d = (updatedAt || "").slice(0, 10);
  if (!d) return "Staršie";
  if (d === todayKey()) return "Dnes";
  const diff = (new Date(todayKey()) - new Date(d)) / 86400000;
  return diff <= 7 ? "Tento týždeň" : "Staršie";
}

// v16 §11 — "Celá história": searchable, pinnable thread list (mock image #2)
function ChatHistoryModal({ onClose, onOpen, onChanged }) {
  const [q, setQ] = useState("");
  const [chats, setChats] = useState(null);
  const aiInitial = (session.characterName || "Ňaňa").charAt(0);
  const load = useCallback(async () => {
    try { setChats((await api("/api/ai/chats" + (q.trim() ? "?query=" + encodeURIComponent(q.trim()) : ""))).chats || []); }
    catch { setChats([]); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  async function pin(id, e) { e.stopPropagation(); try { await api(`/api/ai/chats/${id}/pin`, { method: "POST" }); load(); onChanged && onChanged(); } catch { /* */ } }
  async function del(id, e) { e.stopPropagation(); if (!confirm("Zmazať rozhovor?")) return; try { await api(`/api/ai/chats/${id}`, { method: "DELETE" }); load(); onChanged && onChanged(); } catch { /* */ } }
  const pinned = (chats || []).filter((c) => c.pinned);
  const groups = [];
  for (const c of (chats || [])) { const g = _chatGroup(c.updated_at); let e = groups.find((x) => x.g === g); if (!e) { e = { g, items: [] }; groups.push(e); } e.items.push(c); }
  const avatar = <span style={{ ...st.modalAvatarChip, width: 30, height: 30, borderRadius: "50%", fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{aiInitial}</span>;
  return (
    <Modal title="Poradiť sa" subtitle="celá história" icon="chat" onClose={onClose} maxWidth={460}>
      <div style={{ position: "relative", marginTop: 4 }}>
        <BuzerIcon name="search" size={16} color="var(--neutral-text-subtle)" style={{ position: "absolute", left: 15, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
        <input className="field" style={{ ...st.field, width: "100%", borderRadius: 999, paddingLeft: 40 }} value={q}
          onChange={(e) => setQ(e.target.value)} placeholder="Hľadať v rozhovoroch" />
      </div>
      {chats === null ? <div style={{ ...st.empty, marginTop: 16 }}><div className="spin" /></div> : chats.length === 0 ? (
        <div style={{ ...st.taskDesc, marginTop: 14 }}>{q.trim() ? "Nič sa nenašlo." : "Zatiaľ žiadne rozhovory."}</div>
      ) : (<>
        {pinned.length > 0 && !q.trim() && (
          <div style={{ marginTop: 12 }}>
            <SectionHeader label="Uložené rady" icon="pin" />
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {pinned.map((c) => (
                <button key={c.id} onClick={() => onOpen(c.id)} style={{ flex: "0 0 70%", textAlign: "left", background: "var(--ink)", color: "var(--surface)", border: "none", borderRadius: 14, padding: "11px 13px", cursor: "pointer" }}>
                  <div style={{ fontSize: 10.5, letterSpacing: 1, textTransform: "uppercase", opacity: 0.6, fontFamily: "var(--font-mono)" }}>{(c.updated_at || "").slice(0, 10)}</div>
                  <div style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 14, marginTop: 3, lineHeight: 1.4 }}>{c.title}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {groups.map((grp) => (
          <div key={grp.g} style={{ marginTop: 12 }}>
            <SectionHeader label={grp.g} />
            <div style={st.taskList}>
              {grp.items.map((c) => (
                <div key={c.id} className="task" style={{ ...st.task, ...st.remCard, cursor: "pointer" }} onClick={() => onOpen(c.id)}>
                  {avatar}
                  <span style={st.taskText}>
                    <span style={st.taskLabel}>{c.title}</span>
                    {c.snippet ? <span style={st.taskDesc}>{c.snippet}</span> : null}
                  </span>
                  <button onClick={(e) => pin(c.id, e)} title={c.pinned ? "Odopnúť" : "Uložiť"} style={{ ...st.iconBtn, opacity: c.pinned ? 1 : 0.45 }}><BuzerIcon name="pin" size={15} color={c.pinned ? "var(--accent)" : "currentColor"} /></button>
                  <button onClick={(e) => del(c.id, e)} title="Zmazať" style={st.iconBtn}><Icon name="delete" size={15} color="currentColor" /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </>)}
    </Modal>
  );
}

// v13 A2 / v16 §11 — "Poradiť sa": persistent threaded advisor (last-thread card, chips, full history).
// v23 §1 — type tag + fallback icon for action preview cards (shared across the unified surface)
const KIND_TAG = { create_event: "Udalosť", create_reminder: "Pripomienka", create_habit: "Návyk", create_task: "Úloha", create_routine: "Skupina", create_challenge: "Výzva", create_competition: "Výzva · výsledok" };
const KIND_ICON = { create_event: "calendar", create_reminder: "bell", create_habit: "target", create_task: "onetime", create_routine: "folder", create_challenge: "flag", create_competition: "trophy" };

function AdviseChat({ reload, onOpenPropose, onEdit, categories, initialText, initialChatId }) {
  const [log, setLog] = useState([]);   // [{role:'user'|'ai', text, userText?, suggestions?, pending?, done?, ran?}]
  const [chatId, setChatId] = useState(null);   // current persisted thread (null until first message)
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [threads, setThreads] = useState(null);  // recent threads for the "last conversation" card
  const [showHistory, setShowHistory] = useState(false);
  const [friends, setFriends] = useState([]);    // v23 §2 — for the rodina/skupina picker
  const [picks, setPicks] = useState({});        // `${turnIdx}:${pendingIdx}` -> Set(friend name)
  const [savedNotes, setSavedNotes] = useState({});  // turnIdx -> true (saved raw text as a note)
  const [editDraft, setEditDraft] = useState(null);  // {turnIdx, pIdx, initial} — edit a proposed habit before adding
  const endRef = useRef(null);
  const didInit = useRef(false);
  const loadThreads = useCallback(async () => {
    try { setThreads((await api("/api/ai/chats")).chats || []); } catch { setThreads([]); }
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => {}); }, []);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: "end" }); }, [log, busy]);
  // v23 — opened with a prefilled command (e.g. from a shortcut): send it once
  useEffect(() => { if (initialText && initialText.trim() && !didInit.current) { didInit.current = true; send(initialText); } }, [initialText]);  // eslint-disable-line react-hooks/exhaustive-deps
  // v26.3 §6 — otvorený s konkrétnym vláknom (care check-in z notifikácie): načítaj ho raz
  useEffect(() => { if (initialChatId && !didInit.current) { didInit.current = true; openThread(initialChatId); } }, [initialChatId]);  // eslint-disable-line react-hooks/exhaustive-deps
  async function openThread(id) {
    setBusy(true); setErr("");
    try {
      const r = await api(`/api/ai/chats/${id}`);
      setChatId(id);
      setLog((r.messages || []).map((m) => ({ role: m.role === "assistant" ? "ai" : "user", text: m.text })));
      setShowHistory(false);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  function newChat() { setChatId(null); setLog([]); setInput(""); setErr(""); setPicks({}); setSavedNotes({}); }
  async function send(t) {
    const q = (t == null ? input : t).trim();
    if (!q || busy) return;
    setInput(""); setErr("");
    setLog((p) => [...p, { role: "user", text: q }]); setBusy(true);
    try {
      const r = chatId
        ? await api(`/api/ai/chats/${chatId}/messages`, { method: "POST", body: { message: q, icons: SUGGEST_ICONS } })
        : await api("/api/ai/chats", { method: "POST", body: { message: q, icons: SUGGEST_ICONS } });
      if (!chatId && r.chat_id) setChatId(r.chat_id);
      setLog((p) => [...p, { role: "ai", text: r.reply || r.clarify || "…", userText: q, suggestions: r.suggestions || [], pending: r.pending || [], done: r.done || [] }]);
      if ((r.done || []).length) reload();
      loadThreads();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  function togglePick(key, name) {
    setPicks((p) => { const s = new Set(p[key] || []); if (s.has(name)) s.delete(name); else s.add(name); return { ...p, [key]: s }; });
  }
  async function confirmTurn(idx) {
    const turn = log[idx];
    if (!turn || !(turn.pending || []).length) return;
    setBusy(true); setErr("");
    const done = [...(turn.done || [])];
    for (let j = 0; j < turn.pending.length; j++) {
      const p = turn.pending[j];
      const invite = Array.from(picks[`${idx}:${j}`] || []);  // v23 §2 — ticked rodina/skupina members
      try {
        const r = await api("/api/ai/command/confirm", { method: "POST", body: { token: p.token, invite, chat_id: chatId } });  // chat_id — AI debug: úspešnosť chatu
        if (r.client) { if (r.client.theme) applyAppTheme(r.client.theme); if (r.client.open === "propose" && onOpenPropose) onOpenPropose(); }
        done.push({ summary: r.summary, undo: r.undo, edit: r.client && r.client.edit, calendar: r.calendar });
      } catch (e) { done.push({ summary: "⚠ " + (p.summary || "akcia"), error: true }); }
    }
    setLog((prev) => prev.map((m, i) => i === idx ? { ...m, pending: [], done, ran: true } : m));
    setBusy(false); reload();
  }
  async function saveAsNote(idx, text) {  // v23 §1 — keep the user's raw text if the AI guessed wrong
    const t = (text || "").trim();
    if (!t) return;
    setBusy(true); setErr("");
    try { await api("/api/tasks", { method: "POST", body: { kind: "onetime", label: t.slice(0, 120), due_at: "" } }); setSavedNotes((s) => ({ ...s, [idx]: true })); reload(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }
  const aiName = session.characterName || "Ňaňa";
  const aiInitial = aiName.charAt(0);
  const avatarChip = <span style={{ ...st.modalAvatarChip, width: 32, height: 32, borderRadius: "50%", fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>{aiInitial}</span>;
  const onHome = log.length === 0;
  return (
    // v26.3 — vypĺňa celú výšku pripnutého modalu (ten je priviazaný na visual viewport),
    // takže log(flex:1) scrolluje a vstupné pole ostáva dole nad klávesnicou; hlavička drží hore.
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {showHistory && <ChatHistoryModal onClose={() => setShowHistory(false)} onOpen={openThread} onChanged={loadThreads} />}
      {/* v23 §1 — uprav navrhnutý návyk pred pridaním */}
      {editDraft && (
        <AddCard kind="habit" initial={editDraft.initial} routines={categories || []}
          onAdded={() => {
            const { turnIdx, pIdx, initial } = editDraft;
            setLog((prev) => prev.map((m, i) => i === turnIdx ? { ...m, pending: (m.pending || []).filter((_, k) => k !== pIdx), done: [...(m.done || []), { summary: "Pridané: " + (initial.label || "návyk") }] } : m));
            setEditDraft(null); reload();
          }}
          onCancel={() => setEditDraft(null)} />
      )}
      {/* pripnuté „Nový rozhovor“ — mimo scrollu, drží hore ako hlavička (predtým odscrollovalo s logom) */}
      {!onHome && (
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", paddingBottom: 6, marginTop: 4, borderBottom: "1px solid var(--border)" }}>
          <button type="button" onClick={newChat} style={{ ...st.linkBtn, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="add" size={14} color="currentColor" /> Nový rozhovor</button>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", marginTop: 8 }}>
        {onHome ? (<>
          {/* v21 §2 — „Posledný rozhovor" skrytý: pri prepnutí na tab vyskočila klávesnica a posunula
              modal tak, že sa nezmestil. História ostáva dostupná cez odkaz. */}
          {(threads && threads.length > 0) && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
              <button type="button" onClick={() => setShowHistory(true)} style={{ ...st.linkBtn, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="refresh" size={13} color="currentColor" /> Celá história</button>
            </div>
          )}
          {/* VÝNIMKA (schválené): centrovaný chat-delič — SectionHeader je ľavý s hairline vpravo, tu treba centrovaný oddeľovač konverzácie */}
          <div style={{ textAlign: "center", color: "var(--muted)", letterSpacing: 1.5, fontSize: 11, margin: "16px 0 10px", fontFamily: "var(--font-mono)" }}>NOVÝ ROZHOVOR</div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            {avatarChip}
            <span style={{ flex: 1, background: "var(--ink)", color: "var(--surface)", borderRadius: 16, padding: "11px 13px", fontFamily: "var(--font-ui)", fontSize: 14, lineHeight: 1.5 }}>Napíš čokoľvek — návyk, úlohu, pripomienku, tému, výzvu… alebo sa len poraď. Ukážem ti, čo spravím, a ty to potvrdíš.</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
            {CHAT_CHIPS.map((c) => (
              <button key={c} type="button" onClick={() => send(c)} className="task" style={{ ...st.task, ...st.remCard, cursor: "pointer" }}>
                <span style={{ ...st.taskLabel, flex: 1, textAlign: "left" }}>{c}</span>
                <Icon name="chevron" size={16} color="var(--muted)" />
              </button>
            ))}
          </div>
        </>) : (<>
        {log.map((m, i) => (
          <div key={i} style={{ marginTop: 10 }}>
            {m.role === "user" ? (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <span style={{ background: "var(--accent-tint)", color: "var(--text)", borderRadius: 16, padding: "9px 13px", fontSize: 14.5, maxWidth: "82%" }}>{m.text}</span>
              </div>
            ) : (<>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                {avatarChip}
                <span style={{ flex: 1, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: "11px 13px", fontFamily: "var(--font-ui)", fontSize: 14, color: "var(--text)", lineHeight: 1.5 }}>{m.text}</span>
              </div>
              {(m.suggestions || []).length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6, marginTop: 8, marginLeft: 40 }}>
                  {m.suggestions.map((sg, j) => (
                    <button key={j} type="button" onClick={() => send("Pridaj: " + sg.label)} title={sg.why || ""}
                      style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 999, padding: "8px 14px", fontSize: 13.5, fontWeight: 700, color: "var(--text)", cursor: "pointer", fontFamily: "var(--font-ui)" }}>+ {sg.label}</button>
                  ))}
                </div>
              )}
              {(m.done || []).map((d, j) => (
                <div key={"d" + j} className="task" style={{ ...st.task, ...st.remCard, marginTop: 6 }}>
                  <span style={st.taskIcon}><Icon name={d.error ? "close" : "check"} size={16} color={d.error ? "var(--danger)" : "var(--accent)"} stroke={3} /></span>
                  <span style={st.taskText}><span style={st.taskLabel}>{d.summary}</span>{d.calendar ? <span style={{ ...st.taskDesc, color: "var(--accent)", display: "inline-flex", alignItems: "center", gap: 4 }}><BuzerIcon name="calendar" size={11} color="var(--accent)" />{d.calendar}</span> : null}</span>
                  {d.edit && onEdit && <button onClick={() => onEdit(d.edit)} style={st.linkBtn}>upraviť</button>}
                </div>
              ))}
              {(m.pending || []).length > 0 && (<>
                {m.pending.map((p, j) => {
                  // v23 §1 — rich preview card (icon box + title + detail + type tag); v23 §2 — family/group picker
                  const meta = p.meta;
                  const fb = (meta && KIND_ICON[meta.kind]) || "star2";
                  const hasDetail = meta && (meta.due_at || meta.points != null || meta.recur || meta.routine || meta.metric || meta.timer_min);
                  const pickKey = `${i}:${j}`;
                  const selected = picks[pickKey] || new Set();
                  return (
                    <div key={"p" + j}>
                      <div className="task" style={{ ...st.task, ...(p.confirm ? st.remOverdue : st.remCard), alignItems: "center", marginTop: 6 }}>
                        <span style={st.taskIcon}>{meta && meta.icon ? <Glyph icon={meta.icon} fallback={fb} /> : (p.confirm ? <Icon name="delete" size={18} color="var(--danger)" stroke={3} /> : <BuzerIcon name={fb} size={19} color="var(--text2)" />)}</span>
                        <span style={st.taskText}>
                          <span style={st.taskLabel}>{(meta && meta.label) || p.summary}</span>
                          {hasDetail ? (
                            <span style={{ ...st.taskDesc, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 2, fontFamily: "var(--font-mono)" }}>
                              {meta.due_at && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><BuzerIcon name="clock" size={12} color="var(--muted)" />{fmtDuePretty(meta.due_at)}</span>}
                              {meta.points != null && <span>+{meta.points} b.</span>}
                              {meta.timer_min ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><BuzerIcon name="clock" size={12} color="var(--muted)" />{meta.timer_min} min</span> : null}
                              {meta.recur && <span>{meta.recur}</span>}
                              {meta.routine && <span>{meta.routine}</span>}
                              {meta.metric && <span>{meta.metric}</span>}
                            </span>
                          ) : null}
                          {p.confirm && <span style={{ ...st.taskDesc, color: "var(--danger)" }}>nevratná akcia</span>}
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0, alignSelf: "center" }}>
                          {meta && meta.kind && KIND_TAG[meta.kind] ? <span style={st.planTag}>{KIND_TAG[meta.kind]}</span> : null}
                          {meta && meta.kind === "create_habit" && (
                            <button type="button" title="Upraviť pred pridaním" aria-label="Upraviť"
                              onClick={() => setEditDraft({ turnIdx: i, pIdx: j, initial: { label: meta.label || "", icon: meta.icon || "target", pts: meta.points != null ? meta.points : 15, timer_min: meta.timer_min || 0, ..._aiRecurToFields(meta.recur_code) } })}
                              style={{ ...st.iconBtn, color: "var(--accent)" }}><Icon name="edit" size={15} color="currentColor" /></button>
                          )}
                        </span>
                      </div>
                      {/* v23 §2 — výzva/súťaž pre rodinu/skupinu: označ, koho pozvať (ty si automaticky súčasťou) */}
                      {meta && meta.pick && (
                        <div style={{ marginTop: 6, marginLeft: 4 }}>
                          <div style={{ ...st.taskDesc, marginBottom: 4 }}>Koho beriem? Si automaticky súčasťou.</div>
                          {friends.length === 0 ? <div style={st.taskDesc}>Zatiaľ nemáš priateľov, koho pozvať.</div> : friends.map((f) => {
                            const on = selected.has(f.name);
                            return (
                              <button key={f.friend_id} type="button" onClick={() => togglePick(pickKey, f.name)}
                                style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", background: on ? "var(--accent-tint)" : "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "8px 11px", marginTop: 4, cursor: "pointer", color: "var(--text)" }}>
                                <span style={{ width: 18, height: 18, borderRadius: 5, border: "2px solid " + (on ? "var(--accent)" : "var(--border)"), background: on ? "var(--accent)" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{on && <Icon name="check" size={12} color="var(--on-ember)" stroke={3} />}</span>
                                <span style={{ flex: 1 }}>{f.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button type="button" onClick={() => confirmTurn(i)} disabled={busy} style={{ ...st.primaryBtn, width: "100%", marginTop: 8 }}>
                  {m.pending.some((p) => p.meta && p.meta.pick) ? "Vytvoriť výzvu (+ ja)" : "Potvrdiť a spustiť"}</button>
              </>)}
              {/* v23 §1 — keď AI netrafil: ulož pôvodný text ako poznámku (žiadne odkladanie na neskôr) */}
              {!savedNotes[i] && m.userText && !(m.suggestions || []).length && !(m.done || []).length && (
                <button type="button" onClick={() => saveAsNote(i, m.userText)} disabled={busy} className="task" style={{ ...st.task, ...st.remCard, width: "100%", textAlign: "left", cursor: "pointer", marginTop: 6 }}>
                  <span style={st.taskIcon}><Icon name="onetime" size={18} color="var(--text2)" /></span>
                  <span style={st.taskText}><span style={st.taskLabel}>Nie celkom? Ulož ako poznámku</span></span>
                  <Icon name="chevron" size={16} color="var(--muted)" />
                </button>
              )}
              {savedNotes[i] && <div style={{ ...st.infoNote, marginTop: 6 }}>Uložené ako poznámka do Plánu.</div>}
            </>)}
          </div>
        ))}
        </>)}
        {busy && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10 }}>
            {avatarChip}
            <span style={{ display: "inline-flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: "12px 14px" }}>
              <span className="bz-typing"><i /><i /><i /></span>
              <span style={{ ...st.taskDesc, margin: 0 }}>premýšľam…</span>
            </span>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {err && <div style={{ ...st.err, flexShrink: 0 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexShrink: 0 }}>
        {/* v21 §2 — bez autoFocus; v21.10 — rovnaký radius ako ostatné polia (nie pill/kruh) */}
        <input className="field" style={{ ...st.field, flex: 1 }} value={input} placeholder={"Napíš správu " + askName() + "…"}
          onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }} />
        <MicButton onText={(t) => setInput((p) => (p ? p + " " : "") + t)} disabled={busy} size={46} />
        <button type="button" onClick={() => send()} disabled={busy || !input.trim()} aria-label="Poslať"
          style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 11, border: "none", background: "var(--accent)", color: "var(--on-ember)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: (busy || !input.trim()) ? 0.5 : 1 }}>
          <Icon name="send" size={18} color="var(--on-ember)" /></button>
      </div>
    </div>
  );
}

// v21 §1b — AI recur code → AddCard recurrence fields
const _AI_WD = { po: 1, ut: 2, st: 3, "š": 4, stv: 4, stvr: 4, pi: 5, pia: 5, so: 6, sob: 6, ne: 7, ned: 7 };
function _aiRecurToFields(code) {
  if (code === "i2") return { recur_mode: "interval", recur_n: 2 };
  if (code === "i3") return { recur_mode: "interval", recur_n: 3 };
  if (code === "wc") return { recur_mode: "weekly_count", recur_n: 2 };
  if (code && String(code).toLowerCase().indexOf("wd") === 0) {
    // v26.2 §4 — "wd:po,st,pi" → weekdays; AddCard reads recur_days as a CSV string of 1..7
    const val = String(code).indexOf(":") >= 0 ? String(code).split(":")[1] : "";
    const days = [...new Set(val.split(",").map((s) => s.trim().toLowerCase())
      .map((s) => (/^\d+$/.test(s) ? Number(s) : (_AI_WD[s] || _AI_WD[s.slice(0, 3)] || _AI_WD[s.slice(0, 2)])))
      .filter((d) => d >= 1 && d <= 7))].sort((a, b) => a - b);
    return { recur_mode: "weekdays", recur_days: days.join(",") };
  }
  return { recur_mode: "daily" };
}

function CommandSheet({ reload, onClose, onOpenPropose, onEdit, initialText, initialChatId, categories }) {
  // v23 §1 — one unified AI surface (no quick/chat tabs): AdviseChat sám rozhodne rýchlu akciu vs. radu.
  return (
    <Modal avatar={<BuzerIcon name="ai" size={20} color="var(--on-ember)" />} subtitle="AI asistent" title={"Spýtaj sa " + askName()} onClose={onClose} maxWidth={480} viewportPinned>
      <AdviseChat reload={reload} onOpenPropose={onOpenPropose} onEdit={onEdit} categories={categories} initialText={initialText} initialChatId={initialChatId} />
    </Modal>
  );
}

// v14 AI-4 — assistant name in genitive for "Spýtaj sa …"
const NAME_GENITIVE = { "Ňaňa": "Ňane", "Ďoďo": "Ďoďa" };
function askName() { const n = session.characterName || "Ňaňa"; return NAME_GENITIVE[n] || n; }

const CREATE_OPTIONS = [
  { key: "command", icon: "ai", label: "Spýtaj sa Ňane", desc: "Napíš čokoľvek — vytvorím alebo odporučím (návyk, úlohu, pripomienku…)", overlay: true, ai: true, accent: true, section: "top" },
  { key: "habit", tab: "dnes", icon: "flame", label: "Návyk", desc: "Opakovaný denný návyk za body", section: "create" },
  { key: "skupina", tab: "dnes", icon: "folder", label: "Skupina", desc: "Zoskup návyky", section: "create" },
  { key: "challenge", tab: "dnes", icon: "people", label: "Výzva", desc: "Spoločný cieľ s priateľmi — odškrtávanie alebo vlastné výsledky", section: "create" },
  { key: "plan", tab: "plan", icon: "bell", label: "Úloha / Pripomienka / Udalosť", desc: "Rýchlo — jedno pole a akcie (termín, opakovanie, kategória, zdieľanie)", section: "create" },
  { key: "templates", tab: "dnes", icon: "clipboard", label: "Šablóny návykov", desc: "Pridaj si hotový návyk alebo zdieľaj svoj", overlay: true, section: "ready" },
];
// v32.18 — FAB „+" otvorí speed-dial: rad plávajúcich riadkov (ikona + názov) stúpajúcich
// nad stredovým „+", pozadie mimo riadkov vysedivené a priesvitné. Nahrádza spodný „Čo to bude?" sheet.
function CreateSheet({ onSelect, onClose, lite }) {
  useScrollLock();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // v34 — Lite SMIE vytvárať výzvy/súťaže (Lite len nepočíta body), takže ich tu už neskrývame
  const opts = CREATE_OPTIONS.filter((o) => (!o.ai || session.ai));
  const n = opts.length;
  return createPortal(
    <div className="bz-speeddial" onClick={onClose} role="dialog" aria-modal="true" aria-label="Vytvoriť">
      <div className="bz-speeddial__rows" onClick={(e) => e.stopPropagation()}>
        {opts.map((o, i) => {
          const accent = !!o.accent;
          const label = o.key === "command" ? "Spýtaj sa " + askName() : o.label;
          return (
            <button key={o.key} type="button" onClick={() => onSelect(o)}
              className={`bz-speeddial__row${accent ? " bz-speeddial__row--accent" : ""}`}
              /* riadky najbližšie k „+" (dole) nabehnú prvé */
              style={{ animationDelay: `${(n - 1 - i) * 0.03}s` }}>
              <span className="bz-speeddial__chip"><BuzerIcon name={o.icon} size={18} color="currentColor" /></span>
              <span className="bz-speeddial__label">{label}</span>
            </button>
          );
        })}
      </div>
      <button type="button" className="bz-speeddial__fab" onClick={onClose} aria-label="Zavrieť">
        <Icon name="close" size={26} color="var(--on-ember)" stroke={2.6} />
      </button>
    </div>,
    document.body);
}

// v32.17 — ponuka „Viac" (bodky ⋮ v spodnej lište): rovnaký speed-dial ako FAB, ale zatváracie
// „×" sadá na miesto bodiek (vpravo dole). Obchod je v Lite skrytý (Lite nemá body).
// v33i (task #7) — kým je Lite zapnutý, pribudne rýchle „Vypnúť Lite" (FAQ to už sľubovalo,
// chýbal skutočný prepínač).
function MoreSheet({ lite, onShop, onSettings, onHelp, onChangelog, onContact, onLogout, onLiteOff, onClose }) {
  useScrollLock();
  useModalBackClose(onClose);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const opts = [
    !lite && { key: "shop", icon: "shop", label: "Obchod za body", onClick: onShop },
    lite && { key: "liteoff", icon: "leaf", label: "Vypnúť Lite režim", onClick: onLiteOff },
    { key: "settings", icon: "settings", label: "Nastavenia", onClick: onSettings },
    { key: "help", icon: "bookopen", label: "Pomoc a časté otázky", onClick: onHelp },
    { key: "changelog", icon: "sparkles", label: "Čo je nové", onClick: onChangelog },
    { key: "contact", icon: "mail", label: "Nahlásiť chybu", onClick: onContact },
    { key: "logout", icon: "logout", label: "Odhlásiť sa", onClick: onLogout, danger: true },
  ].filter(Boolean);
  const n = opts.length;
  return createPortal(
    <div className="bz-moredial" onClick={onClose} role="dialog" aria-modal="true" aria-label="Viac">
      <div className="bz-moredial__rows" onClick={(e) => e.stopPropagation()}>
        {opts.map((o, i) => (
          <button key={o.key} type="button" onClick={() => { onClose(); o.onClick && o.onClick(); }}
            className={`bz-moredial__row${o.danger ? " bz-moredial__row--danger" : ""}`}
            /* riadky najbližšie k „×" (dole) nabehnú prvé */
            style={{ animationDelay: `${(n - 1 - i) * 0.03}s` }}>
            <span className="bz-moredial__chip"><BuzerIcon name={o.icon} size={18} color="currentColor" /></span>
            <span className="bz-moredial__label">{o.label}</span>
          </button>
        ))}
      </div>
      <div className="bz-moredial__foot">
        {/* v33b — obal zrkadlí 7 buniek spodnej lišty (bz-bnav--seven): „×" v poslednej bunke
            (Viac/bodky) sadá presne na stred bodiek, nie na okraj pásu. */}
        <div className="bz-moredial__foot-inner">
          <div className="bz-moredial__cell" />
          <div className="bz-moredial__cell" />
          <div className="bz-moredial__cell" />
          <div className="bz-moredial__cell bz-moredial__cell--gap" />
          <div className="bz-moredial__cell" />
          <div className="bz-moredial__cell" />
          <div className="bz-moredial__cell">
            <button type="button" className="bz-moredial__close" onClick={onClose} aria-label="Zavrieť">
              <Icon name="close" size={24} color="currentColor" stroke={2.4} />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body);
}

// ─────────────────────────────────────────────────────────────
//  Oznámenia (zvonček) + Buzeráky (pošťuchnutia)
// ─────────────────────────────────────────────────────────────
const NOTIF_ICON = {
  buzerak: ["buzerak", "var(--ember)"], friend_request: ["addfriend", "var(--plum)"],
  friend_accepted: ["friends", "var(--success)"], challenge_invite: ["challenge", "var(--ember)"],
  shared_item: ["share", "var(--plum)"], shared_event: ["calendar", "var(--info)"],
  badge: ["badge", "var(--honey)"], freeze: ["freeze", "var(--info)"],
  group_add: ["friends", "var(--plum)"], recap: ["comeback", "var(--honey)"],
  mood_nudge: ["moon", "var(--plum)"], shop_afford: ["shop", "var(--honey)"],
  habit_reminder: ["target", "var(--info)"],  // v25 §8 — pripomenutie návyku
  challenge_end: ["flag", "var(--ember)"],    // v26 — výzva ukončená (archív)
  care_checkin: ["ai", "var(--plum)"],        // v27 §2 — príhovor AI postavy
  reaction: ["heart", "var(--ember)"],        // T7 — niekto zareagoval na tvoj status
  levelup: ["star2", "var(--honey)"] };       // v33 — nový level (riadok v zozname má vlastnú ikonu levelu, pozri nižšie)

// v32.19 — oznámenia v štýle FAB speed-dialu, ale ukotvené HORE (najnovšie zvrchu nadol).
// Zvonček → „×" (zavrieť) vpravo hore, kôš (zmazať všetko) vľavo hore. Kompaktné plávajúce riadky.
function NotifInbox({ items, onClose, onSeen, onNavigate, onDelete, onClearAll, highlightId, onPoke }) {
  useScrollLock();
  useModalBackClose(onClose);
  useEffect(() => { onSeen(); }, []);  // mark all read on open
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [hi, setHi] = useState(highlightId || null);  // G6: highlighted row
  const [clearAsk, setClearAsk] = useState(false);  // v31 — potvrdenie „vymazať všetky"
  const rowRef = useRef(null);
  useEffect(() => { if (highlightId) setHi(highlightId); }, [highlightId]);
  useEffect(() => { if (hi && rowRef.current) rowRef.current.scrollIntoView({ block: "center" }); }, [hi]);
  return createPortal(
    <div className="bz-notifdial" onClick={onClose} role="dialog" aria-modal="true" aria-label="Oznámenia">
      <div className={`bz-notifdial__head${items.length === 0 ? " bz-notifdial__head--only" : ""}`}>
        {items.length > 0 && (
          <button type="button" className="bz-notifdial__ctl bz-notifdial__ctl--trash"
            aria-label="Vymazať všetky oznámenia" title="Vymazať všetky"
            onClick={(e) => { e.stopPropagation(); setClearAsk(true); }}>
            <Icon name="delete" size={16} color="currentColor" />
          </button>
        )}
        <button type="button" className="bz-notifdial__ctl bz-notifdial__ctl--close" aria-label="Zavrieť" onClick={onClose}>
          <Icon name="close" size={17} color="currentColor" />
        </button>
      </div>
      {items.length === 0 ? (
        <div className="bz-notifdial__empty">Zatiaľ žiadne oznámenia.</div>
      ) : (
        <div className="bz-notifdial__rows" onClick={(e) => e.stopPropagation()}>
          {items.map((n, i) => {
            const isPoke = n.kind === "buzerak";  // poke otvorí detail modal (+ šťuchnúť späť)
            // v33 — riadok levelup má vlastnú ikonu (ikona toho konkrétneho levelu, nie generická
            // hviezda) — level je zakódovaný v n.link ("levelup:<číslo>").
            const lvlIdx = n.kind === "levelup" ? parseInt(String(n.link || "").split(":")[1], 10) : null;
            return (
              <div key={n.id} ref={hi === n.id ? rowRef : null}
                className={`bz-notifdial__row${hi === n.id ? " bz-notifdial__row--hi" : ""}`}
                style={{ animationDelay: `${Math.min(i, 8) * 0.03}s` }}>
                <button type="button" className="bz-notifdial__main" onClick={() => (isPoke ? onPoke(n) : onNavigate(n.link))}>
                  <span className="bz-notifdial__chip"><Icon name={(lvlIdx && LEVEL_ICON[lvlIdx]) || (NOTIF_ICON[n.kind] || ["bell"])[0]} size={19} color="currentColor" /></span>
                  <span className="bz-notifdial__body">
                    <span style={st.notifBody}>{n.body}</span>
                    {n.created_at && <span style={st.notifTime}>{relTime(n.created_at)}</span>}
                  </span>
                  {!n.seen && <span style={st.notifDot} />}
                </button>
                <button type="button" className="bz-notifdial__del" onClick={() => onDelete(n.id)} title="Odstrániť" aria-label="Odstrániť"><Icon name="close" size={15} color="currentColor" /></button>
              </div>
            );
          })}
        </div>
      )}
      {clearAsk && (
        <Modal center title="Vymazať všetky upozornenia?" icon="trash" maxWidth={400} onClose={() => setClearAsk(false)}>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>Naozaj chceš vymazať všetky oznámenia? Túto akciu nemožno vrátiť.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <Button variant="ghost" block onClick={() => setClearAsk(false)}>Zrušiť</Button>
            <Button variant="destructive" block onClick={() => { onClearAll(); setClearAsk(false); }}>Vymazať všetky</Button>
          </div>
        </Modal>
      )}
    </div>,
    document.body);
}



// ─────────────────────────────────────────────────────────────
//  Úvodný setup (onboarding) — prvé prihlásenie
// ─────────────────────────────────────────────────────────────
const GOAL_CHIPS = [
  { code: "zdravie", icon: "dumbbell", label: "Zdravie" }, { code: "sustredenie", icon: "target", label: "Sústredenie" },
  { code: "spanok", icon: "bed", label: "Spánok" }, { code: "kondicka", icon: "run", label: "Kondička" },
  { code: "pokoj", icon: "meditation", label: "Pokoj" }, { code: "mix", icon: "bread", label: "Z každého rožka troška" },
];
const DIFF_OPTS = [
  { code: "easy", icon: "egg", label: "Rozohrievačka", desc: "Mini dávky — nech to ide hladko." },
  { code: "med", icon: "dumbbell", label: "Mazácky režim", desc: "Slušná výzva každý deň." },
  { code: "hard", icon: "flame", label: "Beštia mód", desc: "Naplno, bez výhovoriek." },
];
function OnboardingWizard({ user, onDone }) {
  const [name, setName] = useState((user && user.name) || "");
  const [gender, setGender] = useState((user && user.gender) || "z");
  const [goals, setGoals] = useState([]);
  const [diff, setDiff] = useState("med");
  const [character, setCharacter] = useState("nana");  // B1: AI assistant choice
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("form");   // O1: "form" → "setup" (install + notifications)
  const [pushDone, setPushDone] = useState(false);
  const installed = isStandalone();
  function toggleGoal(c) {
    setGoals((p) => {
      if (c === "mix") return p.includes("mix") ? [] : ["mix"];
      const next = p.filter((x) => x !== "mix");
      return next.includes(c) ? next.filter((x) => x !== c) : [...next, c];
    });
  }
  async function finish() {
    setBusy(true);
    try {
      await api("/api/onboard", { method: "POST", body: {
        // prázdny výber = prázdny (žiadne návyky); „mix" sa nasadí len keď ho používateľ naozaj zvolí
        name: name.trim(), gender, goals, difficulty: diff } });
      // B1: save the chosen AI assistant
      try {
        const cname = character === "dodo" ? "Ďoďo" : "Ňaňa";
        await api("/api/profile/character", { method: "POST", body: { character } });
        session.character = character; session.characterName = cname;
      } catch (e) { /* non-fatal */ }
      setBusy(false); setStep("setup");   // O1: install/notifications step before entering
    } catch (e) { setBusy(false); }
  }
  async function enableNotifs() {
    if (!installed) { alert("Najprv pridaj appku na plochu, potom zapni upozornenia."); return; }
    await enablePush(); setPushDone(true);
  }
  if (step === "setup") {
    return (
      <div style={st.page}>
        <div style={{ ...st.container, maxWidth: 440, paddingTop: "calc(env(safe-area-inset-top,0px) + 24px)" }}>
          <div style={st.card}>
            <div style={st.obTitle}>Skoro hotovo!</div>
            <div style={st.obSub}>Pridaj si Buzerlístok na plochu a zapni upozornenia, nech ti žiadny návyk neunikne.</div>

            <button onClick={installApp} style={{ ...st.primaryBtn, width: "100%", marginTop: 16, padding: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              Pridať na plochu</button>

            {installed ? (
              <button onClick={enableNotifs} style={{ ...st.ghostBtn, width: "100%", marginTop: 10, padding: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                {pushDone ? <><Chk />Upozornenia zapnuté</> : <><BuzerIcon name="bell" size={16} color="currentColor" /> Zapnúť upozornenia</>}</button>
            ) : (
              <div style={{ ...st.infoNote, marginTop: 10, display: "flex", alignItems: "flex-start", gap: 6 }}><BuzerIcon name="bell" size={15} color="var(--muted)" style={{ flexShrink: 0, marginTop: 1 }} /><span>Upozornenia zapneš po pridaní na plochu — alebo kedykoľvek v <strong>Účet → Upozornenia</strong>.</span></div>
            )}

            <button onClick={onDone} style={{ ...st.primaryBtn, width: "100%", marginTop: 18, padding: 14, fontSize: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}><BuzerIcon name="rocket" size={18} color="var(--on-ember)" /> Začať</button>
            <button onClick={onDone} style={{ ...st.linkBtn, display: "block", width: "100%", textAlign: "center", marginTop: 10 }}>Spravím neskôr v nastaveniach</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={st.page}>
      <div style={{ ...st.container, maxWidth: 440, paddingTop: "calc(env(safe-area-inset-top,0px) + 24px)" }}>
        <div style={st.card}>
          <div style={{ ...st.obTitle, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%" }}><BuzerIcon name="sprout" size={22} color="var(--accent)" /> Vitaj v Buzerlístku!</div>
          <div style={st.obSub}>Za pár sekúnd ti pripravíme návyky na štart.</div>

          <div style={st.obLabel}>Ako ti hovoríme?</div>
          <input className="field" style={st.field} placeholder="Tvoje meno" value={name}
            onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus />
          <div style={{ ...st.seg, marginTop: 8 }}>
            <button type="button" onClick={() => setGender("z")} className="seg-btn" style={{ ...st.segBtn, ...(gender === "z" ? st.segOn : {}) }}>Žena</button>
            <button type="button" onClick={() => setGender("m")} className="seg-btn" style={{ ...st.segBtn, ...(gender === "m" ? st.segOn : {}) }}>Muž</button>
            <button type="button" onClick={() => setGender("x")} className="seg-btn" style={{ ...st.segBtn, ...(gender === "x" ? st.segOn : {}) }}>Iné</button>
          </div>

          <div style={st.obLabel}>Čo chceš zlepšiť?</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {GOAL_CHIPS.map((g) => (
              <button key={g.code} type="button" onClick={(e) => { e.currentTarget.blur(); toggleGoal(g.code); }} className="chip"
                style={{ ...st.chip, flex: "none", ...(goals.includes(g.code) ? st.chipOn : {}), display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name={g.icon} size={15} color="currentColor" /> {g.label}</button>
            ))}
          </div>

          <div style={st.obLabel}>Aké tempo?</div>
          {DIFF_OPTS.map((o) => (
            <button key={o.code} type="button" onClick={(e) => { e.currentTarget.blur(); setDiff(o.code); }} className="sheet-row"
              style={{ ...st.obDiff, ...(diff === o.code ? st.obDiffOn : {}) }}>
              <span className="bz-lead bz-lead--circle"><BuzerIcon name={o.icon} size={20} color="var(--text)" /></span>
              <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                <span style={st.sheetLabel}>{o.label}</span><span style={st.sheetDesc}>{o.desc}</span></span>
              {diff === o.code && <span style={{ display: "inline-flex", alignItems: "center" }}><Icon name="check" size={16} color="var(--accent)" stroke={3} /></span>}
            </button>
          ))}

          {/* B1 — choose the AI assistant */}
          <div style={st.obLabel}>Kto ťa bude motivovať?</div>
          <div style={{ display: "flex", gap: 10, transform: "translateZ(0)", backfaceVisibility: "hidden" }}>
            {[{ k: "nana", n: "Ňaňa", e: "flower", d: "láskavá, povzbudzujúca" }, { k: "dodo", n: "Ďoďo", e: "dumbbell", d: "priamy hecér" }].map((c) => (
              <button key={c.k} type="button" onClick={(e) => { e.currentTarget.blur(); setCharacter(c.k); }}
                style={{ ...st.statCard, ...(character === c.k ? st.streakCard : {}), cursor: "pointer", gap: 2 }}>
                <div style={{ fontSize: 26 }}><BuzerIcon name={c.e} size={26} color="var(--text)" /></div>
                <div className="display" style={{ fontSize: 17, fontWeight: 700, color: character === c.k ? "var(--ember)" : "var(--text)" }}>{c.n}</div>
                <div style={st.statLabel}>{c.d}</div>
              </button>
            ))}
          </div>

          <button onClick={finish} disabled={busy || !name.trim()}
            style={{ ...st.primaryBtn, width: "100%", marginTop: 18, padding: 14, fontSize: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {busy ? "…" : <><BuzerIcon name="rocket" size={18} color="var(--on-ember)" /> Začať</>}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Šablóny návykov (predvolené + tvoje + zdieľané; pridať / zdieľať)
// ─────────────────────────────────────────────────────────────
function TemplateShare({ tid, onClose, onShared }) {
  useModalBackClose(onClose);
  const [friends, setFriends] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => setFriends([])); }, []);
  async function go() { setBusy(true); try { await api("/api/templates/" + tid + "/share", { method: "POST", body: { friend_ids: sel } }); onShared(); } catch (e) { setBusy(false); } }
  return (
    <div style={{ ...st.sheetWrap, zIndex: 70 }} onClick={onClose}>
      <div style={st.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={st.sheetHead}><span style={st.sheetTitle}><TI name="share" color="var(--plum)" />Zdieľať šablónu</span>
          <button onClick={onClose} style={st.sheetClose} aria-label="Zavrieť">✕</button></div>
        {friends === null ? <div style={{ display: "flex", justifyContent: "center", padding: 16 }}><div className="spin" /></div>
          : friends.length === 0 ? <div style={st.taskDesc}>Zatiaľ žiadni priatelia.</div>
          : <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{friends.map((f) => (
              <button key={f.friend_id} onClick={() => setSel((p) => p.includes(f.friend_id) ? p.filter((x) => x !== f.friend_id) : [...p, f.friend_id])}
                className="chip" style={{ ...st.chip, flex: "none", ...(sel.includes(f.friend_id) ? st.chipOn : {}) }}>{sel.includes(f.friend_id) ? <Chk /> : null}{f.name}</button>))}</div>}
        <button onClick={go} disabled={busy || !sel.length} style={{ ...st.primaryBtn, width: "100%", marginTop: 12 }}>{busy ? "…" : "Zdieľať"}</button>
      </div>
    </div>
  );
}

function TemplatesSheet({ onClose, reload }) {
  useModalBackClose(onClose);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [shareFor, setShareFor] = useState(null);
  const load = useCallback(async () => { try { setData(await api("/api/templates")); } catch (e) { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  async function apply(id) { setBusy("a" + id); setErr(""); try { await api("/api/templates/" + id + "/apply", { method: "POST" }); reload(); } catch (e) { setErr(e.message); } setBusy(""); }
  async function del(id) { if (!confirm("Zmazať šablónu?")) return; await api("/api/templates/" + id, { method: "DELETE" }); load(); }
  function Section({ title, items, mine }) {
    if (!items || !items.length) return null;
    return (
      <>
        <div style={{ ...st.fieldHint, marginTop: 10 }}>{title}</div>
        {items.map((t) => (
          <div key={t.kind + t.id} style={st.shopRow}>
            <span className="bz-lead bz-lead--circle"><Glyph icon={t.icon} /></span>
            <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <span style={st.sheetLabel}>{t.label}</span>
              <span style={st.sheetDesc}>{t.descr ? t.descr + " · " : ""}+{t.points} b.{t.owner ? " · od " + t.owner : ""}</span>
            </span>
            {mine && <button onClick={() => setShareFor(t.id)} style={st.iconBtn} title="Zdieľať">👥</button>}
            {mine && <button onClick={() => del(t.id)} style={st.del} title="Zmazať">×</button>}
            <button onClick={() => apply(t.id)} disabled={busy === "a" + t.id} style={{ ...st.doneBtn, flex: "none" }}>{busy === "a" + t.id ? "…" : "Pridať"}</button>
          </div>
        ))}
      </>
    );
  }
  return (
    <div style={st.sheetWrap} onClick={onClose}>
      <div style={st.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={st.sheetHead}><span style={st.sheetTitle}><TI name="onetime" color="var(--ember)" />Šablóny návykov</span>
          <button onClick={onClose} style={st.sheetClose} aria-label="Zavrieť">✕</button></div>
        {err && <div style={st.err}>{err}</div>}
        {!data ? <div style={{ display: "flex", justifyContent: "center", padding: 20 }}><div className="spin" /></div> : (
          <div style={{ maxHeight: "58vh", overflowY: "auto" }}>
            <Section title="Tvoje šablóny" items={data.mine} mine />
            <Section title="Zdieľané s tebou" items={data.shared} />
            <Section title="Predvolené" items={data.system} />
          </div>
        )}
        {shareFor && <TemplateShare tid={shareFor} onClose={() => setShareFor(null)} onShared={() => setShareFor(null)} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Hlavná obrazovka s tabmi
// ─────────────────────────────────────────────────────────────
// ── Reusable modal / popup (F-Modal) ─────────────────────────────
// strip a leading emoji/symbol prefix from a title string (legacy titles like "📂 Rutiny")
// B2/E1 — animated multi-step guided tour with a moving spotlight on the real UI (data-tour anchors)
const TOUR_STEPS = [
  { tab: "dnes", icon: "flame", title: "Vitaj v Buzerlístku!", text: "Krátka prehliadka — ukážem ti, kde čo nájdeš. Poďme na to." },
  { tab: "dnes", icon: "pulse", title: "Denné návyky", target: "hero", text: "Tu plníš svoje návyky, zbieraš body a držíš sériu. Návyky si zoskupíš do skupín — podrž prst na skupine a usporiadaš ich. Riadok séria · level · body ťa prepne do prehľadu.", liteText: "Tu plníš svoje denné návyky. Zoskupíš si ich do skupín — podrž prst na skupine a usporiadaš ich." },
  { tab: "dnes", icon: "feather", title: "Ako sa dnes cítiš?", target: "mood", text: "Klepni na smajlíka a zapíš si dnešnú náladu — a voliteľne aj to, čo ju ovplyvnilo. Neskôr v Prehľade uvidíš svoje nálady v čase." },
  { tab: "dnes", icon: "ai", title: "AI ti pomôže", text: "Pri tvorbe návyku ti AI navrhne názov, ikonu, body aj skupinu. A cez „Navrhni mi návyky“ dostaneš tipy podľa toho, na čo sa chceš zamerať.", liteText: "Pri tvorbe návyku ti AI navrhne názov, ikonu aj skupinu — a vie ti celý návyk vymyslieť aj podľa toho, na čo sa chceš zamerať." },
  { tab: "dnes", icon: "add", title: "Vytvor čokoľvek", target: "fab", text: "Tlačidlom + pridáš návyk, úlohu či pripomienku, alebo spustíš spoločnú výzvu s priateľmi.", liteText: "Tlačidlom + pridáš návyk, úlohu či pripomienku." },
  { tab: "dnes", icon: "settings", title: "Navigácia dole", target: "nav", text: "Dole prepínaš sekcie — Dnes, Prehľad, Organizér, Priatelia a Účet. Stredovým + vytvoríš čokoľvek, bodky vpravo skrývajú obchod, pomoc aj odhlásenie. Farbu témy aj pokojný Lite režim nájdeš v Účte." },
  { tab: "profil", icon: "trophy", title: "Hotovo!", confetti: true, text: "To je všetko. Prehliadku tu kedykoľvek spustíš znova v Účte. Veľa zdaru — a žiadne výhovorky!" },
];

function GuidedTour({ onTab, onClose, lite }) {
  const [i, setI] = useState(0);
  const [spot, setSpot] = useState(null);  // {left,top,width,height} of the highlighted element
  const step = TOUR_STEPS[i];
  const last = i === TOUR_STEPS.length - 1;
  useEffect(() => {
    onTab(step.tab);
    setSpot(null);
    if (!step.target) return;
    let raf;
    const find = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (!el) { raf = requestAnimationFrame(find); return; }
      try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (e) { /* */ }
      setTimeout(() => { const r = el.getBoundingClientRect(); setSpot({ left: r.left - 7, top: r.top - 7, width: r.width + 14, height: r.height + 14 }); }, 320);
    };
    const t = setTimeout(find, 260);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [i]);  // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {spot ? <div style={{ ...st.tourSpot, left: spot.left, top: spot.top, width: spot.width, height: spot.height }} />
            : <div style={st.tourDimAll} />}
      {step.confetti && !REDUCE_MOTION && (
        <div style={st.confettiWrap}>
          {Array.from({ length: 60 }).map((_, k) => (
            <span key={k} className="confetti" style={{ left: `${(k * 37) % 100}%`,
              background: ["var(--confetti-1)", "var(--confetti-2)", "var(--confetti-3)", "var(--confetti-4)", "var(--confetti-5)"][k % 5],
              animationDelay: `${(k % 6) * 0.12}s`, animationDuration: `${1.8 + (k % 5) * 0.25}s` }} />
          ))}
        </div>
      )}
      <div style={st.tourWrap}>
        <div style={st.tourCard} key={i}>
          <button onClick={onClose} style={st.tourClose} aria-label="Zavrieť"><Icon name="close" size={16} color="currentColor" /></button>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={st.tourIcon}><BuzerIcon name={step.icon} size={22} color="var(--text)" /></div>
            <div style={{ fontSize: 17, fontWeight: 800, fontFamily: "var(--font-display)" }}>{step.title}</div>
            <span style={st.tourCount}>{i + 1}/{TOUR_STEPS.length}</span>
          </div>
          <div style={{ ...st.taskDesc, marginTop: 10, fontSize: 14, lineHeight: 1.55 }}>{(lite && step.liteText) || step.text}</div>
          <div style={st.tourDots}>
            {TOUR_STEPS.map((_, k) => (
              <span key={k} style={{ width: k === i ? 18 : 7, height: 7, borderRadius: 999, background: k === i ? "var(--accent)" : "var(--field-border)", transition: "all .25s" }} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            {i > 0 && <button type="button" onClick={() => setI(i - 1)} style={st.ghostBtn}>Späť</button>}
            <button type="button" onClick={() => (last ? onClose() : setI(i + 1))} style={{ ...st.primaryBtn, flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {last ? <><Icon name="check" size={16} color="currentColor" stroke={3} /> Hotovo</> : "Ďalej ›"}</button>
          </div>
        </div>
      </div>
    </>
  );
}

// v23 — hravý onboarding tutoriál: AI sprievodca + spotlight na reálne UI (data-tour) + akčné kroky
const TUT_HABITS = [
  { icon: "water", label: "Vypiť pohár vody", desc: "Aspoň jeden pohár — splníš hneď.", points: 5 },
  { icon: "dumbbell", label: "Sprav 3 kliky", desc: "Krátke a hneď splniteľné.", points: 5 },
  { icon: "sparkles", label: "5 vedomých nádychov", desc: "Zhlboka sa nadýchni a vydýchni 5×.", points: 5 },
];

function useTypewriter(text) {
  const [out, setOut] = useState(REDUCE_MOTION ? text : "");
  useEffect(() => {
    if (REDUCE_MOTION) { setOut(text); return; }
    setOut(""); let i = 0;
    const id = setInterval(() => { i += 1; setOut(text.slice(0, i)); if (i >= text.length) clearInterval(id); }, 20);
    return () => clearInterval(id);
  }, [text]);
  return out;
}

function OnboardTutorial({ s, goTo, reload, onFinish }) {
  const aiName = session.characterName || "Ňaňa";
  // v26 §1 — krok „toto sme ti pripravili": vymenuj konkrétne oblasti z registrácie (users.goals)
  const goalLabels = ((s.user && s.user.goals) ? s.user.goals.split(",") : [])
    .map((c) => (GOAL_CHIPS.find((g) => g.code === c) || {}).label).filter(Boolean);
  // v26 §1 — keď si používateľ pri registrácii nevybral žiadnu oblasť, návyky sa NEnasievajú
  // (prázdny výber = nič); tutoriál to zohľadní a nesľubuje pripravené návyky.
  const hasGoals = !!(s.user && s.user.goals && s.user.goals.trim());
  const seededText = !hasGoals
    ? "Pri registrácii si nevybral/a žiadne oblasti, tak sme ti nič vopred nenaplnili — a to je v pohode. O chvíľu si spolu vytvoríme prvý."
    : goalLabels.length
      ? `Podľa toho, čo si si vybral/a pri registrácii (${goalLabels.join(", ").toLowerCase()}), sme ti pripravili prvé návyky. Kedykoľvek si ich upravíš alebo pridáš vlastné.`
      : "Na základe tvojich odpovedí pri registrácii sme ti pripravili prvé návyky. Kedykoľvek si ich upravíš alebo pridáš vlastné.";
  const STEPS = [
    { kind: "info", text: `Ahoj, som ${aiName}. Ukážem ti to tu — krok po kroku a rýchlo.`, cta: "Poďme na to" },
    { kind: "mood", go: "dnes", target: "mood", text: "Začnime zľahka — dole si zaklikni, ako sa dnes cítiš." },
    { kind: "info", go: "dnes", target: "habit", text: seededText, cta: "Ďalej" },
    { kind: "pick", go: "dnes", text: "Super! Vyber si svoj prvý návyk — splníš ho hneď teraz." },
    { kind: "complete", go: "dnes", target: "habit", text: "Skvelé. Teraz ten návyk naozaj splň — a až potom si ho odškrtni. Máš svoj prvý splnený návyk." },
    { kind: "reminder", go: "dnes", target: "fab", topBubble: true, text: "Skús si pridať pripomienku — ťukni na +, vyber „Pripomienka“ a vyplň ju. Keď ju uložíš, ozvem sa.", skip: true },
    { kind: "info", go: "dnes", target: "menu", text: "Cez toto menu sa prepneš medzi sekciami — Dnes, Prehľad, Pripomienky, Správy aj Priatelia.", cta: "Ďalej" },
    { kind: "info", go: "prehlad", target: "level", text: "Toto je Prehľad. Práve si získal/a svoj prvý level — Totálne newbie (Level 1)! Nájdeš tu úroveň, body, série aj odznaky.", cta: "Ďalej" },
    { kind: "info", go: "prehlad", target: "table", text: "V tabuľke vidíš celý týždeň naraz — ktoré návyky si ktorý deň splnil/a.", cta: "Ďalej" },
    { kind: "info", go: "priatelia", target: "invite", topBubble: true, text: "A nakoniec — cez Pridať priateľa pozvi kamaráta alebo si pridaj existujúceho. Spolu sa to zvláda lepšie.", cta: "Ďalej", skip: true },
    { kind: "done", go: "prehlad", target: "badge-tutorial", text: "A je to — teraz to tu poznáš. Toto je tvoj odznak za zvládnutý tutoriál!", cta: "Hotovo", confetti: true },
  ];
  const [i, setI] = useState(() => Math.min(Math.max(0, s.tutorial_step || 0), STEPS.length - 1));
  const [pickedId, setPickedId] = useState(null);  // v26 §1 — id návyku z kroku „pick"
  const [spot, setSpot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kbFocus, setKbFocus] = useState(false);   // text input zameraný (klávesnica) → bublinu hore
  const [dialogOpen, setDialogOpen] = useState(false);  // otvorený modal → bublinu schovaj zaň
  const skippedRef = useRef(false);
  const basePts = useRef(s.totalPoints || 0);
  const routineBase = useRef(null);  // počet rutín pri vstupe do kroku „rutina" → po vytvorení posuň ďalej
  const reminderBase = useRef(null);  // počet pripomienok pri vstupe do kroku „reminder" → po vytvorení chváľ + Pokračovať
  const step = STEPS[i];
  const last = step.kind === "done";
  // akčné kroky: počkaj na akciu (nálada / splnený návyk), potom ukáž „Pokračovať" (neposúvaj samo —
  // nech sa neprekrýva s otvoreným „Prečo?" panelom po výbere nálady)
  const actionDone = (step.kind === "mood" && !!s.mood)
    || (step.kind === "complete" && (s.totalPoints || 0) > basePts.current)
    || (step.kind === "reminder" && reminderBase.current != null
        && ((s.reminders || []).length + (s.onetime || []).length) > reminderBase.current);
  // po splnení návyku presuň spotlight z návyku na level-up banner (fokus na nový level)
  const curTarget = (step.kind === "complete" && actionDone) ? "levelup"
    : (step.kind === "reminder" && actionDone) ? null  // po vytvorení len pochvala (bublina), bez spotlightu
    : (step.target === "table" && !(s.habits || []).length) ? null  // bez návykov tabuľka neexistuje → nesvieť
    : (step.target === "habit" && !(s.habits || []).length) ? "habit-empty"  // bez návykov zvýrazni sekciu „Zatiaľ žiadne návyky"
    : step.target;  // „done": svieť na odznak vždy — aj zamknutý (nech vidia, o čo prišli)
  const displayText = (step.kind === "mood" && actionDone)
    ? "Super. Pokojne pripíš aj prečo — keď budeš chcieť, pokračuj."
    : (step.kind === "complete" && actionDone)
      ? "Hotovo! Získal/a si svoj prvý level — Totálne newbie. Poďme ďalej."
      : (step.kind === "reminder" && actionDone)
        ? "Paráda, vytvoril/a si svoju prvú pripomienku! 🔔 Nájdeš ju v sekcii Pripomienky. Poďme ďalej."
        : (step.kind === "done" && skippedRef.current)
          ? "A je to — teraz to tu poznáš! 🎉 Toto je odznak za zvládnutý tutoriál — tentoraz ti unikol, lebo si zopár krokov vynechal/a. Nabudúce je tvoj!"
          : (step.target === "level" && (s.totalPoints || 0) < 5)  // preskočený návyk → žiadny level; netvrď, že ho má
            ? "Toto je Prehľad — nájdeš tu úroveň, body, série aj odznaky. Prvý level (Totálne newbie) ti cvakne hneď po prvom splnenom návyku."
            : (step.target === "table" && !(s.habits || []).length)  // bez návykov tabuľka ani nadpis nie sú
              ? "V Prehľade nájdeš aj týždennú tabuľku návykov — ukáže ti, čo si ktorý deň splnil/a, hneď ako si pridáš prvý návyk."
              : step.text;
  const typed = useTypewriter(displayText);

  useEffect(() => { api("/api/tutorial", { method: "POST", body: { step: i } }).catch(() => {}); }, [i]);
  // v23 — po dosiahnutí posledného kroku (ak nič nepreskočené) hneď udeľ odznak, nech ho vieme zvýrazniť
  const awardedRef = useRef(false);
  useEffect(() => {
    if (step.kind === "done" && !skippedRef.current && !awardedRef.current) {
      awardedRef.current = true;
      api("/api/tutorial", { method: "POST", body: { completed: true } }).then(reload).catch(() => {});
    }
  }, [step.kind]);  // eslint-disable-line react-hooks/exhaustive-deps
  // sleduj zameranie text-inputu (klávesnica) a otvorený modal
  useEffect(() => {
    const onIn = (e) => { const t = e.target; if (t && /^(INPUT|TEXTAREA)$/.test(t.tagName)) setKbFocus(true); };
    const onOut = () => setKbFocus(false);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    const checkDlg = () => setDialogOpen(!!document.querySelector('[role="dialog"]'));
    checkDlg();
    const obs = new MutationObserver(checkDlg);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); obs.disconnect(); };
  }, []);
  // navigácia na správnu obrazovku (len pri zmene kroku)
  useEffect(() => { if (step.go) goTo(step.go); }, [i]);  // eslint-disable-line react-hooks/exhaustive-deps
  // spotlight SLEDUJE cieľ pri scrollovaní/resize; keď je otvorený modal, zvýraznenie schovaj (vráti sa po zatvorení)
  useEffect(() => {
    setSpot(null);
    if (!curTarget || dialogOpen) return;
    let raf, alive = true, scrolled = false;
    // v26 §1 — v kroku „splň návyk" mieri spotlight len na VYBRANÝ návyk ([data-taskid]);
    // fallback = pôvodný kontajner zoznamu (data-tour="habit"), keby riadok nebol v DOM
    const getEl = () => (step.kind === "complete" && !actionDone && pickedId != null
      && document.querySelector(`[data-taskid="${pickedId}"]`))
      || document.querySelector(`[data-tour="${curTarget}"]`);
    const measure = () => {
      const el = getEl();
      if (!el) return;
      const r = el.getBoundingClientRect();
      setSpot({ left: r.left - 8, top: r.top - 8, width: r.width + 16, height: r.height + 16 });
    };
    const find = () => {
      if (!alive) return;
      const el = getEl();
      if (!el) { raf = requestAnimationFrame(find); return; }
      if (!scrolled) { scrolled = true; try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (e) { /* */ } }
      setTimeout(measure, 360);
    };
    const t = setTimeout(find, 320);
    const onMove = () => measure();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => { alive = false; clearTimeout(t); if (raf) cancelAnimationFrame(raf); window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove); };
  }, [i, curTarget, dialogOpen, pickedId, actionDone]);  // eslint-disable-line react-hooks/exhaustive-deps

  // pri prechode kroku zatvor čokoľvek otvorené (Prečo panel, formulár…) — Modaly počúvajú Escape
  function closeOverlays() { try { document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" })); } catch (e) { /* */ } }
  // krok „reminder": zapamätaj počet pripomienok pri vstupe do kroku; keď pribudne nová,
  // krok sa splní (actionDone) → pochvala + „Pokračovať". Prázdne otvorenie/zatvorenie formulára
  // krok už neposúva — čakáme na REÁLNE vytvorenie pripomienky.
  useEffect(() => {
    reminderBase.current = step.kind === "reminder" ? ((s.reminders || []).length + (s.onetime || []).length) : null;
  }, [i]);  // eslint-disable-line react-hooks/exhaustive-deps
  function advance() { closeOverlays(); setI((x) => Math.min(x + 1, STEPS.length - 1)); }
  function skip() {
    skippedRef.current = true; closeOverlays();
    // preskočenie „vyber návyk" preskočí aj nadväzujúci „splň návyk" — bez vybraného návyku nedáva zmysel
    const jump = step.kind === "pick" ? 2 : 1;
    setI((x) => Math.min(x + jump, STEPS.length - 1));
  }
  async function pick(h) {
    setBusy(true);
    try {
      const r = await api("/api/tasks", { method: "POST", body: { kind: "habit", icon: h.icon, label: h.label, desc: h.desc, points: h.points } });
      if (r && r.id) setPickedId(r.id);  // v26 §1 — krok „splň" zvýrazní len tento riadok
      await reload();
    }
    catch (e) { /* */ }
    setBusy(false); advance();
  }
  const showSkip = !last && (step.kind === "mood" || step.kind === "complete" || step.kind === "pick" || step.skip);
  // bublina hore, ak je zvýraznený prvok v dolnej polovici (nech neprekrýva FAB / náladu / panel Prečo);
  // pri kroku „nálada" vždy hore — po výbere sa otvorí panel Prečo + klávesnica, nech ich text neprekryje
  const vh = (typeof window !== "undefined" ? window.innerHeight : 800);
  // bublina hore: pri nálade vždy; pri písaní (klávesnica) tiež; inak ak je cieľ v dolnej polovici
  const bubbleTop = step.kind === "mood" || step.topBubble || kbFocus || (spot && (spot.top + spot.height / 2) > vh / 2);
  // keď je otvorený modal (napr. formulár pripomienky), AI daj ZA modal — okrem nálady, kde AI vysvetľuje panel Prečo
  const behindModal = dialogOpen && step.kind !== "mood";
  const wrapStyle = { ...st.tourWrap, zIndex: behindModal ? 40 : 620,
    ...(bubbleTop ? { top: "calc(env(safe-area-inset-top,0px) + 14px)", bottom: "auto" } : {}) };

  return (
    <>
      {spot ? <div style={{ ...st.tourSpot, left: spot.left, top: spot.top, width: spot.width, height: spot.height }} /> : <div style={st.tourDimAll} />}
      {step.confetti && !REDUCE_MOTION && (
        <div style={st.confettiWrap}>{Array.from({ length: 60 }).map((_, k) => (
          <span key={k} className="confetti" style={{ left: `${(k * 37) % 100}%`, background: ["var(--confetti-1)", "var(--confetti-2)", "var(--confetti-3)", "var(--confetti-4)", "var(--confetti-5)"][k % 5], animationDelay: `${(k % 6) * 0.12}s`, animationDuration: `${1.8 + (k % 5) * 0.25}s` }} />
        ))}</div>
      )}
      <div style={wrapStyle}>
        <div style={st.tourCard} key={i}>
          <button onClick={() => onFinish(false)} style={st.tourClose} aria-label="Zavrieť"><Icon name="close" size={16} color="currentColor" /></button>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ ...st.modalAvatarChip, width: 34, height: 34, borderRadius: "50%", fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 700, fontSize: 16, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{aiName.charAt(0)}</span>
            <div style={{ flex: 1, minWidth: 0, fontFamily: "var(--font-ui)", fontSize: 14, lineHeight: 1.5, color: "var(--text)", minHeight: 46 }}>{typed}</div>
          </div>
          {step.kind === "pick" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
              {TUT_HABITS.map((h) => (
                <button key={h.label} type="button" disabled={busy} onClick={() => pick(h)} className="task" style={{ ...st.task, ...st.remCard, cursor: "pointer", textAlign: "left" }}>
                  <span style={st.taskIcon}><Glyph icon={h.icon} fallback="target" /></span>
                  <span style={st.taskText}><span style={st.taskLabel}>{h.label}</span><span style={st.taskDesc}>{h.desc}</span></span>
                </button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center" }}>
            {showSkip && !actionDone && <button type="button" onClick={skip} style={st.ghostBtn}>Preskočiť</button>}
            <span style={{ flex: 1 }} />
            {(step.cta || actionDone) && (
              <button type="button" onClick={() => (last ? onFinish(!skippedRef.current) : advance())}
                style={{ ...st.primaryBtn, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, paddingLeft: 22, paddingRight: 22 }}>
                {last ? <><BuzerIcon name="party" size={16} color="currentColor" /> {step.cta}</> : (actionDone ? "Pokračovať ›" : step.cta + " ›")}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// PR7 — is the app running installed (added to home screen)?


// ── PWA: install prompt + web-push subscription ──────────────────

// Step-by-step "add to home screen" guide for existing users (onboarding is skipped
// for them). Uses the native install prompt when the browser offers one (Android /
// desktop Chrome), otherwise shows manual steps for the detected platform.

// v22.2 — silent re-subscribe on app load: if the user already granted notifications, make sure a
// valid push subscription exists on the server (browsers rotate/expire endpoints; the server prunes
// dead ones on 410, after which the user silently stops getting pushes until they re-subscribe).

// v32.17 — top bar (wordmark + zvonček oznámení). Navigácia žije v spodnej lište.
// v30 — na Dnes/Prehľade (non-Lite) wordmark nahradí kontextový titulok (DNES · deň / PREHĽAD).
function TopBar({ s, title, onBell, onHome }) {
  return (
    <div style={st.topBar}>
      <button type="button" onClick={onHome} className="bz-wordmark" aria-label="Domov — Dnes" title="Dnes">{title || <>BUZERLÍSTOK{s.lite_mode && <span style={st.liteTag}>LITE</span>}</>}</button>
      <IconButton variant="surface" size="sm" onClick={onBell} aria-label="Oznámenia" title="Oznámenia" style={{ position: "relative" }}>
        <Icon name="bell" size={16} color="currentColor" />
        {s.unread_notifications > 0 && <span style={st.topDot} />}
      </IconButton>
    </div>
  );
}


// v26 — Pomoc / časté otázky (položka v bočnom menu pred „Nahlásiť chybu/návrh")
const FAQ_ITEMS = [
  { q: "Čo je Buzerlístok?", a: "Tvoj osobný parťák na návyky: každý deň si odškrtávaš návyky, zbieraš body, buduješ série a levely. K tomu pripomienky a udalosti, spoločné výzvy s priateľmi, nálady, odznaky a AI asistent, ktorý ti pomôže s čímkoľvek — od vymyslenia návyku po radu, keď sa nedarí." },
  { q: "Ako stiahnem appku na plochu?", a: "Buzerlístok funguje ako appka priamo z prehliadača. Na iPhone (Safari): klikni na Zdieľať (štvorček so šípkou) → „Na plochu“. Na Androide (Chrome): menu ⋮ vpravo hore → „Pridať na plochu / Inštalovať“. Návod nájdeš aj v Účet → Inštalovať appku." },
  { q: "Ako zapnem upozornenia?", a: "Najprv si appku pridaj na plochu (viď vyššie), potom v Účte povoľ upozornenia. V Účet → Upozornenia si vyberieš, ktoré typy chceš dostávať. Ak upozornenia nechodia, skontroluj nastavenia systému — stíšenie alebo šetrič batérie ich vedia blokovať." },
  { q: "Ako aktualizujem appku?", a: "Appka sa aktualizuje sama pri otvorení. Ak máš pocit, že vidíš staršiu verziu, potiahni stránku nadol pre obnovenie, prípadne appku zavri a otvor znova. Aktuálne číslo verzie vidíš dole v tomto menu (napr. „verzia 26“)." },
  { q: "Ako fungujú body a levely?", a: "Za každý splnený návyk získaš jeho body. Body sa sčítavajú do celkového skóre, ktoré ťa posúva po leveloch (Prehľad → Úroveň). Body vieš minúť v Obchode na farby, písma a animácie — minuté body ťa o level nepripravia, level rastie z celkovo nazbieraných." },
  { q: "Čo je séria a ako ju nestratím?", a: "Séria rastie za každý deň, keď splníš všetko naplánované. Ak potrebuješ deň voľna, návyk môžeš Preskočiť (prázdny krúžok vo farbe témy) — preskočenie sériu nepreruší, preruší ju až vynechanie. Pomôže aj Záchranný kruh: automaticky zachráni jeden vynechaný deň." },
  { q: "Čo sú záchranné kruhy a ako ich získam?", a: "Záchranný kruh ti automaticky zachráni jeden vynechaný deň, takže sa ti nepreruší séria — nemusíš nič robiť, použije sa sám, keď treba. Získaš ich dvoma spôsobmi: 1) zadarmo za každých 7 dní tvojej najdlhšej série (vždy keď prekonáš ďalší 7-dňový míľnik, pribudne ti jeden), a 2) kúpou v Obchode za body. Keď nejaký získaš alebo sa použije, appka ti dá vedieť upozornením." },
  { q: "Ako si nastavím pripomenutie návyku?", a: "V úprave návyku zapni „Upozorniť ma v čase“ a vyber čas. Upozornenie príde len vtedy, keď návyk ešte nie je splnený a v ten deň sa má robiť. Pri výzvach si čas nastavuje každý člen sám v detaile výzvy." },
  { q: "Čo je Lite režim?", a: "Zjednodušený režim bez bodov a levelov — sleduje sa len denná séria. Zapneš ho v Účte; kým je zapnutý, vieš ho rýchlo vypnúť aj z bočného menu. Body sa v Lite nepočítajú ani na pozadí." },
  { q: "Ako fungujú výzvy?", a: "Výzva je spoločný cieľ s priateľmi a má dva spôsoby zapisovania. Pri „Splnení“ každý deň iba odškrtávaš, či si výzvu splnil, a v rebríčku vidíte série a body. Pri „Vlastnom výsledku“ zapisuješ denné čísla (napr. km, minúty) a vyhráva najlepší súčet. Frekvenciu (denne, X× do týždňa alebo vlastné dni) určuje zakladateľ." },
  { q: "Ako zdieľam pripomienku alebo udalosť?", a: "Pri vytváraní zapni „Zdieľať s priateľom“ a vyber ľudí. Vznikne jedna spoločná položka — keď ju ktokoľvek splní alebo vlastník upraví, zmena sa prejaví všetkým." },
  { q: "Ako získam odznaky?", a: "Odznaky sa udeľujú automaticky v 10 kategóriách (splnenia, série, body, výzvy, priatelia, nálady, plánovanie, perfektné týždne, vernosť a špeciálne). V Prehľade vidíš pri každej kategórii progres k ďalšiemu odznaku." },
];

function HelpModal({ onClose }) {
  return (
    <Modal title="Pomoc a časté otázky" icon="bookopen" onClose={onClose} maxWidth={460}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {FAQ_ITEMS.map((f, idx) => (
          <details key={idx} style={{ background: "var(--surface2)", border: "1px solid var(--field-border)", borderRadius: 12, padding: "10px 14px" }}>
            <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{f.q}</summary>
            <div style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.55, color: "var(--text2)" }}>{f.a}</div>
          </details>
        ))}
      </div>
      <div style={{ ...st.fieldHint, marginTop: 14 }}>Nenašiel/a si odpoveď? Napíš nám cez „Nahlásiť chybu/návrh“ v menu — ozveme sa na tvoj e-mail.</div>
    </Modal>
  );
}

// v28 — changelog zobrazený po ťuknutí na číslo verzie v menu. Najnovšie hore.
// Pri každom vydaní pridaj nový blok na začiatok (typ: "feat" = novinka, "fix" = oprava).
// Historické záznamy sú vyčítané z commitov (zoskupené a preložené do reči používateľa).
const CHANGELOG = [
  { label: "verzia 28", date: "18. 7. 2026", items: [
    { t: "feat", s: "V rebríčku výzvy/súťaže vidíš všetkých účastníkov (nielen troch) a miniatúry ukazujú aj tvoje ozdoby — prstenec levelu, korunku či žiaru." },
    { t: "feat", s: "Prepracovaný verejný profil: dlhý status sa zalomí, pribudli spoloční priatelia, cez „⋮“ vieš priateľa odobrať, a keď niekto zareaguje na tvoj status, príde ti upozornenie s presmerovaním." },
    { t: "feat", s: "Krajšie a čitateľnejšie detaily návyku/výzvy/súťaže: jednotné pilulky s údajmi, popis v ráme, prehľadnejší týždenný postup a pripomienka priamo medzi údajmi." },
    { t: "feat", s: "V chate má každá správa čas a jednotlivé dni sú oddelené nadpisom (Dnes / Včera / dátum)." },
    { t: "feat", s: "Lite režim môže vytvárať výzvy aj súťaže (aj cez AI asistenta) a má pokojnejšie texty; body sa v Lite naďalej nepočítajú." },
    { t: "feat", s: "Kategória je pri tvorbe výzvy/súťaže povinná s jasným upozornením; nové účty sú rovno Community bez časového obmedzenia." },
    { t: "feat", s: "Jednotný, čitateľnejší vzhľad všetkých upozornení na Dnes a väčšie pole na status v nastaveniach." },
    { t: "fix", s: "Súťaž sa dá presunúť do skupiny/rutiny a meniť jej poradie v rámci skupiny." },
    { t: "fix", s: "Okno „Šťuchnúť“ pri písaní vlastnej správy už neodskočí nad obrazovku — sadne tesne nad klávesnicu." },
    { t: "fix", s: "Upozornenie „Presúvaj podržaním“ ostáva viditeľné aj počas úprav týždennej tabuľky." },
  ] },
  { label: "verzia 27.6", date: "18. 7. 2026", items: [
    { t: "feat", s: "Keď v týždennej tabuľke upravuješ splnenia a odídeš bez „Hotovo“ (na Zoznam, iný tab či cez logo), appka sa spýta, či zmeny uložiť — už sa ticho nezahodia." },
  ] },
  { label: "verzia 27", date: "16. 7. 2026", items: [
    { t: "feat", s: "Rýchle pridávanie z „+“: jedno pole a ikonky na termín, opakovanie, kategóriu a zdieľanie — spraví z toho úlohu, pripomienku alebo udalosť. Aj udalosť môže mať pripomienku." },
    { t: "feat", s: "Verejný profil sa celý farbí podľa tvojho levelu — hlavička aj obsah." },
    { t: "feat", s: "Perfektný deň má krajší kruh s poletujúcimi iskričkami; ťuknutím naň sa môžeš pochváliť priateľom." },
    { t: "feat", s: "„Súhrn nálad“ si otvoria všetci (nielen Lite) — vidíš najčastejšiu náladu za 7 dní aj celkovo." },
    { t: "feat", s: "Spodná lišta je jeden plný pruh a tlačidlo „+“ sadá nižšie; ikony sú výraznejšie." },
    { t: "feat", s: "V súťaži vidíš body pre víťaza a tvorca má akcie (Upraviť · Vyhodnotiť · zmazať) vždy poruke dole." },
    { t: "feat", s: "Kopa vylepšení Lite režimu: dátum v hlavičke, „Perfektný deň“, séria a perfektné dni v mape aktivity." },
    { t: "feat", s: "Do menu pribudlo „Čo je nové“ (toto okno) a Nastavenia." },
    { t: "fix", s: "Znova opravené vykresľovanie odznakov na niektorých Androidoch." },
  ] },
  { label: "verzia 26.25", date: "12. 7. 2026", items: [
    { t: "feat", s: "Keď máš všetko splnené (a hotové skryté), na Dnes sa ukáže deň-kruh — a vidno, koľkatý perfektný deň po sebe máš." },
    { t: "feat", s: "Sekcia AI asistenta na Dnes je nenápadnejšia — vo farbe témy, ako ostatné upozornenia." },
    { t: "feat", s: "V oznámeniach pribudlo „Vymazať všetky“." },
    { t: "feat", s: "Stopky a režim „viac zápisov za deň“ sa dajú zapnúť aj na existujúcej výzve — doterajšie zápisy zostanú." },
    { t: "feat", s: "Číslo verzie v menu je teraz výrazné a klikateľné (otvorí toto okno)." },
    { t: "fix", s: "Upozornenie „Máš dosť bodov na…“ chodí už len v appke, bez pushu na telefón." },
    { t: "fix", s: "Zvýraznenia a tlačidlá zjednotené do farby témy (žiadny fialový nádych)." },
  ] },
  { label: "verzia 26.21", date: "12. 7. 2026", items: [
    { t: "feat", s: "Súťaže so stopkami: časomer meria výsledok a po zastavení sa zápis uloží sám." },
    { t: "feat", s: "Návyky so zápisom za deň: pripomienka po uložení a prehľad zápisov za daný deň." },
    { t: "feat", s: "Ťuknutie na číslo verzie v menu otvorí toto okno „Čo je nové“." },
    { t: "feat", s: "V detaile pripomienky s viacerými dátumami vidíš všetky termíny naraz." },
    { t: "feat", s: "V stopkových súťažiach vidíš pri každom účastníkovi počet záznamov (behov), nie počet dní." },
    { t: "feat", s: "Menej otravné upozornenia: pripomienka nálady je len v appke a namiesto viacerých pripomienok cez deň príde jedna večerná, keď treba." },
    { t: "fix", s: "AI asistent sa už nezasekáva a pridá aj konkrétnejší návyk — napr. „piť 2 litre vody“, aj keď už máš „pitie vody“." },
    { t: "fix", s: "AI hláška na Dnes sedí na dennú dobu (žiadne „v tichu noci“ ráno) a je bez emoji." },
    { t: "fix", s: "Zvýraznenie aktuálneho dňa v kalendári a banner pozvánky do výzvy sú vo farbe témy." },
  ] },
  { label: "verzia 26", date: "11. 7. 2026", items: [
    { t: "feat", s: "Verejný profil sa mení s levelom — prstenec, hlavička a míľnikové ozdoby." },
    { t: "feat", s: "Dlhé podržanie tlačidla „+“ otvorí AI asistenta." },
    { t: "feat", s: "Pripomienka bez dátumu = obyčajná poznámka." },
    { t: "feat", s: "Lite používatelia vidia súťaže (výzvy so značením výsledku)." },
    { t: "feat", s: "Zmena mena priamo v Účte." },
    { t: "feat", s: "Rebríček aktivity počíta kombinované skóre — spravodlivejšie poradie." },
    { t: "feat", s: "Vo výbere priateľov vidíš profilovku a náhľad profilu." },
    { t: "fix", s: "E-mail priateľov a členov výziev sa už nikomu nezobrazuje." },
  ] },
  { label: "Vylepšenia", date: "10. 7. 2026", items: [
    { t: "feat", s: "Pripomienky: mesačné, ročné aj vlastné (viac dátumov) opakovanie." },
    { t: "feat", s: "Kalendár ukazuje aj úlohy s termínom a samostatnú sekciu „Bez termínu“." },
    { t: "feat", s: "Aj pripomienky môžu mať kategóriu; appka si pamätá záložku Zoznam/Kalendár." },
    { t: "feat", s: "Výzvy: náhľad pozvánky, týždenná mriežka plnenia a bohatší archív." },
    { t: "feat", s: "Prekopaný tutoriál a štartovacie balíčky návykov pre nových používateľov." },
  ] },
  { label: "Vylepšenia", date: "8.–9. 7. 2026", items: [
    { t: "feat", s: "Oslavný „deň-kruh“ so segmentovým pokrokom pri plnení návykov." },
    { t: "feat", s: "Nahlásiť chybu/návrh priamo z menu — s odpoveďou na e-mail." },
    { t: "fix", s: "V AI rozhovore ostáva „Nový rozhovor“ pripnutý hore; zdieľané udalosti si držia nastavenie upozornení." },
  ] },
  { label: "Veľký redizajn", date: "1. 7. 2026", items: [
    { t: "feat", s: "Úplne nový vzhľad appky — prehľadnejšie Dnes (Zoznam aj Týždeň), karty a detaily návykov, výziev a pripomienok." },
    { t: "feat", s: "Obchod s kozmetikou: rámiky a ozdoby profilu za body; verejný profil s odznakmi." },
    { t: "feat", s: "AI priamo v tvorbe návyku (vymysli návyk, odhadni body) a merateľné AI návyky." },
    { t: "feat", s: "Lite režim bez bodov a levelov; mesačný PDF report s grafmi v Prehľade." },
    { t: "feat", s: "Upozornenia vedia preniesť rovno na miesto (obchod, level, prehľad)." },
  ] },
  { label: "Prvé opravy", date: "15.–27. 6. 2026", items: [
    { t: "fix", s: "Opravené vykresľovanie profilu na niektorých Androidoch a scrollovanie dlhých okien." },
    { t: "fix", s: "Spoľahlivejší výber ikon a rýchlejšie načítanie po aktualizácii." },
  ] },
];

function ChangelogModal({ onClose }) {
  return (
    <Modal title="Čo je nové" icon="sparkles" onClose={onClose} maxWidth={460}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {CHANGELOG.map((rel, i) => (
          <div key={i}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: "var(--text)" }}>{rel.label}</span>
              <span style={{ fontSize: 12, color: "var(--muted)" }}>{rel.date}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rel.items.map((it, j) => {
                return (
                  <div key={j} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Tag tone={it.t === "feat" ? "brand" : "neutral"} size="xs" style={{ flexShrink: 0, marginTop: 1 }}>{it.t === "feat" ? "Novinka" : "Oprava"}</Tag>
                    <span style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--text2)" }}>{it.s}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...st.fieldHint, marginTop: 14 }}>Máš nápad alebo si našiel/la chybu? Napíš nám cez „Nahlásiť chybu/návrh“ v menu.</div>
    </Modal>
  );
}


function ProfileMenu({ me, refreshing, onProfile, onPublic, onRefresh, onLogout, onClose }) {
  useModalBackClose(onClose);
  return (
    <div style={st.sheetWrap} onClick={onClose}>
      <div style={st.sheet} onClick={(e) => e.stopPropagation()}>
        {/* v26.3 §15 — ťuk na meno + fotku otvorí Účet (rovnaká akcia ako „Môj účet") */}
        <button type="button" onClick={onProfile}
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "4px 8px 12px", width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
          aria-label="Otvoriť účet">
          <Avatar user={me} size={44} />
          <span style={{ ...st.sheetLabel, fontSize: 19, fontFamily: "var(--font-display)" }}>{me.name || "Účet"}</span>
        </button>
        <button className="sheet-row" style={st.sheetRow} onClick={onProfile}>
          <span className="bz-lead bz-lead--circle"><Icon name="settings" size={21} color="var(--text2)" /></span>
          <span style={{ flex: 1, textAlign: "left" }}><span style={st.sheetLabel}>Môj účet</span></span>
          <span style={st.sheetChevron}>›</span>
        </button>
        <button className="sheet-row" style={st.sheetRow} onClick={onPublic}>
          <span className="bz-lead bz-lead--circle"><Icon name="friends" size={21} color="var(--plum)" /></span>
          <span style={{ flex: 1, textAlign: "left" }}><span style={st.sheetLabel}>Verejný profil</span></span>
          <span style={st.sheetChevron}>›</span>
        </button>
        <button className="sheet-row" style={st.sheetRow} onClick={onRefresh} disabled={refreshing}>
          <span className="bz-lead bz-lead--circle"><span style={{ display: "inline-flex", animation: refreshing ? "r 0.8s linear infinite" : "none" }}><Icon name="refresh" size={20} color="var(--text2)" /></span></span>
          <span style={{ flex: 1, textAlign: "left" }}><span style={st.sheetLabel}>{refreshing ? "Obnovujem…" : "Obnoviť obsah"}</span></span>
        </button>
        <button className="sheet-row" style={st.sheetRow} onClick={onLogout}>
          <span className="bz-lead bz-lead--circle"><Icon name="logout" size={21} color="var(--danger)" /></span>
          <span style={{ flex: 1, textAlign: "left" }}><span style={{ ...st.sheetLabel, color: "var(--danger)" }}>Odhlásiť sa</span></span>
        </button>
      </div>
    </div>
  );
}

function Main({ user, onLogout }) {
  const narrow = useIsNarrow();
  const HOME_TAB = "dnes";  // primary/home tab (first in TABS); back-button returns here
  const [s, setS] = useState(null);
  const [tab, setTab] = useState(HOME_TAB);
  // v32.17 — „Prehľad" je samostatný tab (zdroj pravdy = `tab`); BuzerTab odvodí stats-view z tabu.
  // v32.9 — typewriter titulku pri KAŽDOM príchode na Dnes (unmount pri odchode ho resetne)
  const [homeReset, setHomeReset] = useState(0);      // v26.3 §9 — nonce: klik na logo zhodí Týždeň → Zoznam
  const [showHelp, setShowHelp] = useState(false);      // v32.17 — Pomoc/FAQ (bolo v bočnom menu)
  const [showChangelog, setShowChangelog] = useState(false);  // v32.17 — Čo je nové (bolo v bočnom menu)
  const [tour, setTour] = useState(false);            // B2: guided tour (manual replay)
  const [tourClosed, setTourClosed] = useState(false); // B2: dismissed this session
  const [tutDismissed, setTutDismissed] = useState(false);  // v23 — hravý tutoriál zatvorený v tejto session
  const [appBurst, setAppBurst] = useState(null);     // v24 — animácia pri otvorení appky (z obchodu)
  // v23 — navigácia tutoriálu medzi obrazovkami
  function tutGoTo(dest) {
    setShowCreate(false);
    if (dest === "prehlad") { setTab("prehlad"); }
    else if (dest === "plan") { setTab("plan"); }
    else if (dest === "priatelia") { setFriendsSub("ludia"); setTab("priatelia"); }
    else { setTab("dnes"); }
  }
  // A4 / v33h — po každom prepnutí tabu doscrolluj úplne navrch (aj po opätovnom paint-e; deep-link
  // na Nastavenia scrolluje dole až po ~60 ms, takže sa nebijú).
  useEffect(() => { const go = () => window.scrollTo(0, 0); go(); const r = requestAnimationFrame(go); return () => cancelAnimationFrame(r); }, [tab]);
  // v34 — nedokončené úpravy tabuľky (Dnes → Týždeň, edit mód): BuzerTab si sem zaregistruje strážcu,
  // ktorý pri odchode na iný tab vyvolá potvrdenie „Uložiť zmeny?" namiesto tichého zahodenia.
  const tableGuardRef = useRef(null);
  // v17 §1 — drawer navigation (Štatistiky removed; Týždeň is reached via the in-screen toggle)
  function doNavTo(dest) {
    setCreateWhat(null);  // v9 — plain menu navigation must not carry a pending "+ create" intent (else the add form auto-opens)
    // v33h — opätovný ťuk na už aktívny tab (napr. Účet po deep-linku na Nastavenia) doscrolluje hore
    if (dest === tab) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    // v32.17 — „Prehľad" je samostatný tab v spodnej lište (predtým Dnes v režime prehľadu)
    if (dest === "prehlad") { setTab("prehlad"); return; }
    // A2 — ťuk na „Priatelia" pri neprečítanej správe otvorí rovno sekciu Správy (zoznam konverzácií),
    // nie konkrétnu konverzáciu (žiadny chatFocus).
    if (dest === "priatelia" && (s.unread_messages || 0) > 0) setFriendsSub("spravy");
    setTab(dest);
  }
  function navTo(dest) {
    // v34 — pri skutočnej zmene tabu daj BuzerTab šancu zachytiť neuložené úpravy tabuľky
    if (dest !== tab && tableGuardRef.current && tableGuardRef.current(() => doNavTo(dest))) return;
    doNavTo(dest);
  }
  const [showNotifs, setShowNotifs] = useState(false);  // 🔔 inbox
  const [pokeNotif, setPokeNotif] = useState(null);     // v32.18 — poke detail z oznámenia
  const [profilFocus, setProfilFocus] = useState(null);  // v2 — deep-link into Profil (e.g. "shop:CODE")
  const [notifHighlight, setNotifHighlight] = useState(null);  // G6: row to highlight in the inbox
  const [levelUpOpen, setLevelUpOpen] = useState(null);  // v33 — level (číslo) z ťuknutia na notifikáciu "levelup" v zvončeku
  const [showProfileMenu, setShowProfileMenu] = useState(false);  // 👤 nav profile popup
  const [showCreate, setShowCreate] = useState(false);  // + opens the "Čo chceš vytvoriť?" bottom sheet
  const [showQuickPlan, setShowQuickPlan] = useState(false);  // v33h — rýchle vytvorenie Úloha/Pripomienka/Udalosť
  const [showMore, setShowMore] = useState(false);      // v32.17 — ponuka „Viac" (bodky ⋮ v spodnej lište)
  const [showShop, setShowShop] = useState(false);      // v33b — Obchod z „Viac": samostatný modal (bez skoku na tab Účet)
  const [createWhat, setCreateWhat] = useState(null);   // form key chosen in the sheet
  const [createKey, setCreateKey] = useState(0);        // nonce so the target tab re-opens its form
  const [showTemplates, setShowTemplates] = useState(false);
  const [showContact, setShowContact] = useState(false);  // F5: report-a-bug popup
  const [showCommand, setShowCommand] = useState(false);  // v12 AI command bar
  const [cmdPreload, setCmdPreload] = useState("");       // v12 — preload text (from deferred inbox)
  const [cmdChatId, setCmdChatId] = useState(null);       // v26.3 §6 — otvor konkrétne AI vlákno (care check-in)
  const [editRef, setEditRef] = useState(null);           // v12 — {kind,id} of an AI-created task to edit
  const [friendsSub, setFriendsSub] = useState(null);     // v18 §4 — focus a Priatelia sub-view (e.g. "ludia" from a friend-request notif)
  const [chatFocus, setChatFocus] = useState(null);       // v26.2 §1 — open a specific chat thread from a notification
  const [planFocus, setPlanFocus] = useState(null);       // P — deep-link into a shared note (plan::<id>): id to flash
  const [planFocusKey, setPlanFocusKey] = useState(0);    // P — nonce so re-tapping the same note re-triggers the flash
  const [deepLinkCompId, setDeepLinkCompId] = useState(null);  // G5 — open a competition's detail on Dnes (challenge_overtake link vyzva:<cid>)
  // v32.21 — GLOBÁLNY verejný profil: jeden modal + jedno miesto pre akcie (Šťuchnúť/správa/výzva)
  const [profileUid, setProfileUid] = useState(null);     // otvorené uid profilu (null = zavreté)
  const [profilePoke, setProfilePoke] = useState(null);   // {id,name} → BuzerakSheet (Šťuchnúť)
  const [profileChal, setProfileChal] = useState(null);   // uid → ChallengeCreate (Výzva)
  const [profileHi, setProfileHi] = useState(null);       // T7 — emoji reakcie na zvýraznenie (z notifikácie)
  const [removeFriendAsk, setRemoveFriendAsk] = useState(null);  // T6 — {fid,name} potvrdenie odobratia
  // T7 — open(uid, {reaction}) zvýrazní danú reakciu v profile; bežné open(uid) highlight nenastaví
  const openProfile = useCallback((uid, opts) => { if (uid != null) { setProfileUid(uid); setProfileHi((opts && opts.reaction) || null); } }, []);
  const closeProfile = useCallback(() => { setProfileUid(null); setProfileHi(null); }, []);
  function goFriends(sub) { setFriendsSub(sub || "ludia"); setTab("priatelia"); }
  // v26.2 §1 — single navigation entry point for the bell, push taps (?go=) and boot deep-links.
  // link = "tab[:sub[:id]]"; old 1/2-part links keep working (id undefined → no focusEl).
  function navigateTo(link) {
    setShowNotifs(false);
    if (link && link.startsWith("/u/")) { window.location.href = link; return; }
    const [t, sub, id] = String(link || "").split(":");
    if (t === "shop") { setProfilFocus(link); setTab("profil"); return; }  // v2 — shop:CODE → shop item
    // v32.17 — Správy a Výzvy žijú ako pod-sekcie Priateľov (žiadne vlastné taby)
    if (t === "chat" || t === "spravy") { setTab("priatelia"); setFriendsSub("spravy"); if (t === "chat" && sub) setChatFocus(sub); return; }
    if (t === "vyzvy") { setTab("priatelia"); setFriendsSub("vyzvy"); return; }
    if (t === "priatelia") { if (sub) setFriendsSub(sub); setTab("priatelia"); if (id) focusEl(`[data-friendid="${id}"]`); return; }
    if (t === "prehlad") {
      setTab("prehlad");
      if (sub === "odznaky") focusEl(id ? `[data-badge="${id}"]` : '[data-scroll="odznaky"]', '[data-scroll="odznaky"]');
      else if (sub === "archiv") focusEl('[data-scroll="archiv"]');
      return;
    }
    // G5 — challenge_overtake push (link "vyzva:<cid>"): open that competition's detail on Dnes.
    // Replaces the old "dnes:vyzva:<id>" path for joined participants (that targeted a [data-chalinvite]
    // element which only exists for pending invites, not for someone already in the competition).
    if (t === "vyzva" && sub) { setTab("dnes"); setDeepLinkCompId(Number(sub)); return; }
    // chalchat:<cid> — z notifikácie „chat výzvy ožil": pridaj do skupinového chatu a otvor vlákno.
    // Free používateľ (chat je Premium → join 402) padne na detail výzvy, kde uvidí kontext.
    if (t === "chalchat" && sub) {
      api("/api/challenges/" + sub + "/chat/join", { method: "POST" })
        .then((r) => { setTab("priatelia"); setFriendsSub("spravy"); setChatFocus(String(r.id)); })
        .catch(() => { setTab("dnes"); setDeepLinkCompId(Number(sub)); });
      return;
    }
    if (t === "dnes") {
      setTab("dnes");  // v26.2 — mood/výzva/návyk žijú v Dnes zozname, nie v Prehľade
      if (sub === "vyzva" && id) focusEl(`[data-chalinvite="${id}"]`);
      else if (sub === "mood") focusEl('[data-scroll="mood"]');
      else if (id) focusEl(`[data-taskid="${id}"]`);
      return;
    }
    // P — shared-note deep-link (plan::<id>): force the Zoznam view + flash the note (PlanTab reacts to
    // planFocus/planFocusKey). Bare focusEl couldn't find it when PlanTab was in the calendar view.
    if (t === "plan") { setTab("plan"); if (id) { setPlanFocus(Number(id)); setPlanFocusKey((k) => k + 1); } return; }
    if (t === "ai" && sub) { openCommandThread(parseInt(sub, 10)); return; }  // v26.3 §6 — care check-in vlákno
    // v33 — ťuknutie na "levelup" notifikáciu v zvončeku znova otvorí TEN ISTÝ farebný modal
    // (nie navigáciu na inú obrazovku — level-up je samostatná oslava, nezávislá od Dnes/Prehľadu).
    if (t === "levelup" && sub) { setLevelUpOpen(parseInt(sub, 10)); return; }
    // T7 — "profile:<uid>:<emoji>" (reakcia na status): otvor globálny verejný profil daného
    // používateľa a zvýrazni v ňom danú reakciu. Emoji je 3. časť (obsahuje ':'? nie — emoji bez ':').
    if (t === "profile" && sub) { openProfile(parseInt(sub, 10), { reaction: id || null }); return; }
    if (t) setTab(t);
  }
  function openCommand(text) { setCmdChatId(null); setCmdPreload(text || ""); setShowCommand(true); }
  function openCommandThread(id) { setCmdPreload(""); setCmdChatId(id); setShowCommand(true); }  // v26.3 §6
  // #10 — dlhé podržanie FAB otvorí AI asistenta (krátky ťuk = Vytvoriť)
  const fabLong = useLongPress(() => openCommand());
  // kým držíš FAB, vypni výber textu — inak dlhé podržanie začne označovať text
  // v práve otvorenom AI chate (obsah sa vyrenderuje pod stále stlačeným prstom)
  const endFabHold = () => {
    document.body.classList.remove("fab-holding");
    try { window.getSelection && window.getSelection().removeAllRanges(); } catch { /* noop */ }
  };
  const fabHandlers = {
    onPointerDown: (e) => { document.body.classList.add("fab-holding"); fabLong.handlers.onPointerDown(e); },
    onPointerUp: (e) => { fabLong.handlers.onPointerUp(e); endFabHold(); },
    onPointerLeave: (e) => { fabLong.handlers.onPointerLeave(e); endFabHold(); },
    onPointerCancel: endFabHold,
    onPointerMove: fabLong.handlers.onPointerMove,
    onContextMenu: fabLong.handlers.onContextMenu,
  };
  function onCreateSelect(o) {
    setShowCreate(false);
    if (o.key === "command") { setShowCommand(true); return; }
    if (o.key === "templates") { setShowTemplates(true); return; }
    if (o.key === "plan") { setShowQuickPlan(true); return; }  // v33h — rýchle vytvorenie (Úloha/Pripomienka/Udalosť)
    // v2/v32.17 — cieľový tab (napr. Dnes) sa nastaví nižšie cez setTab, čím sa opustí Prehľad
    // v32.17 — „Napísať správu" cieli Správy, ktoré teraz žijú pod Priateľmi
    if (o.tab === "spravy") { setTab("priatelia"); setFriendsSub("spravy"); setCreateWhat(o.key); setCreateKey((k) => k + 1); return; }
    setTab(o.tab); setCreateWhat(o.key); setCreateKey((k) => k + 1);
  }
  const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(async () => { setS(await api("/api/state?date=" + todayKey())); }, []);
  useEffect(() => { reload(); }, [reload]);
  // v32.21 — akcie globálneho profilového modalu (jedno miesto pre celú appku).
  // Modal zavrieme a spustíme príslušný tok appky (chat / výzva / zdieľaný kalendár / poke).
  const profileActions = useMemo(() => ({
    message: async (uid) => {
      closeProfile();
      try { const r = await api("/api/conversations", { method: "POST", body: { member_ids: [uid] } }); navigateTo("chat:" + r.id); }
      catch (e) { /* */ }
    },
    // Item 1 — Šťuchnúť NEZATVÁRA profil: nudge sheet sa STACKuje NAD verejný profil (aj keď je
    // profil otvorený z chatu — predtým closeProfile pri otvorenom full-screen ChatView (z 200) nechal
    // BuzerakSheet (z 60) skrytý za chatom a pôsobil ako návrat do Správ). Po odoslaní (onSent) sa sheet
    // zavrie a profil ostáva. BuzerakSheet dostane zIndex nad scrim profilu (bz-scrim = 300).
    poke: (uid, name) => { setProfilePoke({ id: uid, name }); },
    create: (uid) => { closeProfile(); setProfileChal(uid); },   // Výzva/súťaž s priateľom
    addFriend: async (uid) => {
      closeProfile();
      try { await api("/api/friends/request", { method: "POST", body: { user_id: uid } }); reload(); }
      catch (e) { /* */ }
    },
    // T6 — odobrať z priateľov: otvor potvrdenie (samotné DELETE + zatvorenie/refresh sú nižšie)
    removeFriend: (fid, name) => { setRemoveFriendAsk({ fid, name }); },
  }), [closeProfile, reload]);
  // v22.2 — keep the push subscription alive so expired endpoints re-register automatically
  useEffect(() => { ensurePushSubscribed(); }, []);
  // I1 — clear the phone's notification tray once on load (opening the app "reads" the pushes)
  useEffect(() => { clearNotifications(); }, []);
  // v22.9 — apply the user's server theme once on load (new users default to green; also
  // fixes a theme carrying over from another account on the same browser). One-time, so a fresh
  // theme pick isn't reverted by polling.
  const _themeApplied = useRef(false);
  useEffect(() => {
    if (s && !_themeApplied.current) {
      _themeApplied.current = true;
      try { if (s.theme) applyAppTheme(s.theme); } catch { /* */ }
      try { applyFont(s.font || ""); } catch { /* */ }   // v24 — odomknuté písmo
      // v24 — animácia pri otvorení appky (raz za načítanie), len ak nie je redukovaný pohyb
      if (s.open_effect && !REDUCE_MOTION) { setAppBurst(s.open_effect); setTimeout(() => setAppBurst(null), 2800); }
    }
  }, [s]);
  // v22.6 — actively pick up new deploys: re-check the service worker on load, when the tab regains
  // focus, and hourly. autoUpdate then activates the new SW and reloads — no manual cache refresh.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const check = () => navigator.serviceWorker.getRegistration().then((r) => r && r.update()).catch(() => {});
    check();
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    const iv = setInterval(check, 60 * 60 * 1000);
    return () => { document.removeEventListener("visibilitychange", onVis); clearInterval(iv); };
  }, []);
  // v21 — keep notifications/counts live while the app is open: poll every 45s + refresh when the
  // tab regains focus (so the bell updates without a manual page reload).
  usePoll(() => { reload().catch(() => {}); }, 45000, true);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") { reload().catch(() => {}); clearNotifications(); }  // I1 — also wipe the tray when returning to foreground
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, [reload]);
  // v15 §7 — keyboard-aware overlays: expose the on-screen keyboard height as --kb-inset so every
  // bottom-sheet modal/sheet (which pad/cap by it) stays fully visible above the keyboard on mobile.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const root = document.documentElement;
    const update = () => {
      const gap = window.innerHeight - vv.height - vv.offsetTop;
      root.style.setProperty("--kb-inset", (gap > 80 ? Math.round(gap) : 0) + "px");
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    // v25 §7.1 — some mobile browsers close the keyboard without firing a visualViewport
    // resize, leaving a stale non-zero --kb-inset that pads every overlay's bottom. When no
    // editable element has focus (checked shortly after focusout), force the inset to 0.
    const settle = () => {
      const el = document.activeElement;
      if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) {
        update();  // re-measure; with the keyboard gone this lands at 0
        const gap = window.innerHeight - vv.height - vv.offsetTop;
        if (gap <= 80) root.style.setProperty("--kb-inset", "0px");
      }
    };
    const onFocusOut = () => { setTimeout(settle, 300); };
    document.addEventListener("focusout", onFocusOut);
    // v26 §3.17 — keyboard can also close via system back/gesture/app switch with NO focusout:
    // re-settle on geometry changes + window focus/blur, and run a slow watchdog so a stale
    // inset can't push centered modals off-middle for more than a moment.
    try { vv.addEventListener("geometrychange", update); } catch (e) { /* older browsers */ }
    window.addEventListener("focus", settle);
    window.addEventListener("blur", onFocusOut);
    const watchdog = setInterval(() => {
      if ((parseInt(root.style.getPropertyValue("--kb-inset")) || 0) > 0) settle();
    }, 1500);
    update();
    return () => {
      vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update);
      try { vv.removeEventListener("geometrychange", update); } catch (e) { /* */ }
      window.removeEventListener("focus", settle); window.removeEventListener("blur", onFocusOut);
      clearInterval(watchdog);
      document.removeEventListener("focusout", onFocusOut); root.style.removeProperty("--kb-inset");
    };
  }, []);
  // v8 — apply the server-saved colour theme once state arrives (localStorage already applied pre-paint)
  // v15 §8 — if the server still has the (now-hidden) dark theme, coerce it to green and persist once.
  useEffect(() => {
    if (!s || !s.theme) return;
    applyAppTheme(s.theme);  // v17 — black is a normal accent now; no coercion
  }, [s && s.theme]);
  // deep link: /i/<token> → /?go=<tab> → open that tab and clean the URL.
  // Also handle the Stripe checkout return (/?billing=success|cancel).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const go = q.get("go");
    // v26.2 §1 — route push taps through the same handler as the bell (id-aware links).
    if (go) navigateTo(decodeURIComponent(go));
    // G6 — deep link from a poke notification: open the inbox and highlight that row
    const nid = q.get("notif");
    if (nid) { setShowNotifs(true); setNotifHighlight(Number(nid)); }
    const billing = q.get("billing");
    if (billing === "success") { setTab("profil"); setTimeout(reload, 1500); alert("Vitaj v Premium! 🎉 Platbu spracúvame — môže chvíľu trvať, kým sa všetko aktivuje."); }
    if (go || billing || nid) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  // keep the bell / chat badge / counts fresh in the background (every 45s).
  // Safe: forms keep their own local state, and the active tab isn't remounted (key=refreshKey).
  usePoll(reload, 45000, true);

  // Back-button override: 1st back from any sub-screen → home tab; on home → confirm before exit.
  // Tabs are React state (not URLs), so we keep one synthetic history entry and re-arm it on pop.
  const tabRef = useRef(tab); tabRef.current = tab;
  const createRef = useRef(showCreate); createRef.current = showCreate;
  useEffect(() => {
    const arm = () => window.history.pushState({ buzer: 1 }, "");
    arm();
    const onPop = () => {
      // v32.23 — any open Modal/Sheet (habit form, detail dialog, shop, …) takes priority: Back
      // just closes the top-most one and leaves tab/section untouched (registered generically by
      // useModalBackClose in Modal.jsx/ds/Sheet.jsx — no per-call-site wiring needed).
      if (hasOpenModal()) { closeTopModal(); arm(); return; }
      if (createRef.current) { setShowCreate(false); arm(); return; }   // close the create sheet first
      if (tabRef.current !== HOME_TAB) { setTab(HOME_TAB); arm(); return; }  // any sub-screen → home
      if (window.confirm("Naozaj chceš odísť z aplikácie?")) { window.history.back(); }  // on home → leave
      else { arm(); }                                                    // stayed → re-arm
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  async function refresh() {
    setRefreshing(true);
    try { await reload(); } catch (e) { /* ignore */ }
    setRefreshKey((k) => k + 1);  // remount active tab → its own data re-fetches too
    setRefreshing(false);
  }
  const [logoutAsk, setLogoutAsk] = useState(false);
  function logout() { setShowProfileMenu(false); setLogoutAsk(true); }
  async function doLogout() { await api("/api/logout", { method: "POST" }); onLogout(); }

  if (!s) return <div style={{ ...st.page, display: "flex", justifyContent: "center", padding: "80px 16px" }}><div className="spin" /></div>;
  if (!s.onboarded) return <OnboardingWizard user={s.user || user} onDone={reload} />;
  const me = s.user || user;
  // v33h — tutoriál DOČASNE VYPNUTÝ (revert: vráť pôvodný výraz na ďalšom riadku)
  const tutActive = false;
  // const tutActive = !s.tutorial_done && !s.lite_mode && !tutDismissed;  // v23 — hravý onboarding tutoriál

  return (
    <ProfileModalProvider uid={profileUid} onOpen={openProfile} onClose={closeProfile} actions={profileActions} highlightReaction={profileHi}>
    <div style={narrow ? { ...st.page, paddingLeft: 11, paddingRight: 11 } : st.page}>
      {appBurst && <EffectBurst effect={appBurst} />}
      <div style={st.container}>
        <TopBar s={s} onHome={() => { setTab("dnes"); setHomeReset((n) => n + 1); }} onBell={() => setShowNotifs(true)}
          /* v30/v32.19 — kontextový titulok, typewriter pri každom vstupe: Dnes / Prehľad / Organizér / Social / Účet */
          title={tab === "plan" ? <Typewriter text="ORGANIZÉR" />
            : tab === "priatelia" ? <Typewriter text="SOCIAL" />
            : tab === "profil" ? <Typewriter text="ÚČET A NASTAVENIA" />
            /* v33h — Lite: Prehľad = „PREHĽAD" + LITE tag; Dnes = dnešný dátum (rovnaký wordmark font
               ako ostatné taby) + LITE tag (ds Tag) */
            : tab === "prehlad"
            ? (s.lite_mode
                ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Typewriter text="PREHĽAD" /><Tag tone="brand" size="xs">LITE</Tag></span>
                : <Typewriter text="PREHĽAD" />)
            : tab === "dnes" && s.lite_mode
            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Typewriter text={fmtDayLong().toUpperCase()} /><Tag tone="brand" size="xs">LITE</Tag></span>
            : tab === "dnes"
            ? (() => { const t = `DNES · ${new Date().toLocaleDateString("sk-SK", { weekday: "long" }).toUpperCase()}`;
                return <Typewriter text={t} />; })()
            : undefined} />
        <div style={{ paddingBottom: 96 }} key={refreshKey}>
          {/* v32.17 — Dnes aj Prehľad renderuje BuzerTab; tab je zdroj pravdy (Prehľad = stats view),
              vnútorná šípka Dnes⇄Prehľad prepína rovno tab. */}
          {(tab === "dnes" || tab === "prehlad") && <BuzerTab s={s} reload={reload} quote={s.daily_quote || quote} createWhat={createWhat} createKey={createKey} homeView={tab === "prehlad" ? "stats" : "dnes"} setHomeView={(v) => setTab(v === "stats" ? "prehlad" : "dnes")} homeReset={homeReset} onOpenCommand={openCommand} onGoFriends={() => goFriends("ludia")} onNotifNavigate={navigateTo} deepLinkCompId={deepLinkCompId} onDeepLinkCompHandled={() => setDeepLinkCompId(null)} navGuard={tableGuardRef} />}
          {tab === "plan" && <PlanTab s={s} reload={reload} createWhat={createWhat} createKey={createKey} planFocus={planFocus} planFocusKey={planFocusKey} />}
          {/* v32.17 — Správy a Výzvy žijú ako pod-sekcie Priateľov */}
          {tab === "priatelia" && <SocialTab s={s} reload={reload} me={me} createWhat={createWhat} createKey={createKey} focusSub={friendsSub} onFocusHandled={() => setFriendsSub(null)} chatFocus={chatFocus} onChatFocusHandled={() => setChatFocus(null)} />}
          {tab === "profil" && <ProfilHub s={s} user={user} focus={profilFocus} onFocusHandled={() => setProfilFocus(null)} onClose={() => setTab("dnes")} onLogout={logout} reload={reload} onTour={() => setTour(true)} onHelp={() => setShowHelp(true)} onContact={() => setShowContact(true)} onChangelog={() => setShowChangelog(true)} onTutorial={() => { setTutDismissed(false); setTab("dnes"); api("/api/tutorial", { method: "POST", body: { done: false, step: 0 } }).then(reload).catch(() => {}); }} onOpenCommand={openCommand} />}
        </div>
        {/* v32.17 — spodná navigácia s vykrojeným stredovým „+" (FAB); nahrádza bočné menu */}
        <BottomNav active={tab} onTab={navTo} me={me}
          planCount={s.reminders.filter((r) => !r.done && !(r.plan_type === "event" && (r.end_at || r.due_at || "").slice(0, 10) < todayKey())).length + s.onetime.length}
          friendsCount={(s.friend_requests || 0) + (s.unread_messages || 0)}
          fabHandlers={fabHandlers} fabTourAttr={{ "data-tour": "fab" }}
          onFab={() => { if (fabLong.fired.current) { fabLong.fired.current = false; return; } setShowCreate(true); }}
          onMore={() => setShowMore(true)} moreOpen={showMore} />
        {showCreate && <CreateSheet lite={!!s.lite_mode} onSelect={onCreateSelect} onClose={() => setShowCreate(false)} />}
        {showQuickPlan && <QuickPlan categories={[...new Set([...(s.onetime || []), ...(s.reminders || [])].map((t) => t.category).filter(Boolean))]} onAdded={reload} onClose={() => setShowQuickPlan(false)} />}
        {showMore && <MoreSheet lite={!!s.lite_mode}
          onShop={() => setShowShop(true)}
          onSettings={() => { setProfilFocus("settings"); setTab("profil"); }}
          onHelp={() => setShowHelp(true)} onChangelog={() => setShowChangelog(true)} onContact={() => setShowContact(true)} onLogout={logout}
          onLiteOff={async () => { await api("/api/profile/lite", { method: "POST", body: { lite: false } }); reload(); }}
          onClose={() => setShowMore(false)} />}
        {/* v33b — Obchod otvorený z „Viac" ako samostatný modal na úrovni App: neprepína viditeľný
            tab na Účet (predtým setProfilFocus("shop")+setTab("profil") preblysklo Účet). */}
        {showShop && <ShopSheet s={s} onClose={() => setShowShop(false)} onBought={reload} />}
        {logoutAsk && (
          <Sheet center title="Odhlásiť sa?" icon="logout" onClose={() => setLogoutAsk(false)}
            footer={<>
              <Button variant="ghost" onClick={() => setLogoutAsk(false)}>Zrušiť</Button>
              <Button variant="destructive" block onClick={doLogout}><Icon name="logout" size={16} color="currentColor" />Odhlásiť</Button>
            </>}>
            <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>Naozaj sa chceš odhlásiť z Buzerlístka?</div>
          </Sheet>
        )}
        {showCommand && <CommandSheet reload={reload} initialText={cmdPreload} initialChatId={cmdChatId} categories={s.categories || []} onClose={() => { setShowCommand(false); setCmdPreload(""); setCmdChatId(null); }}
          onOpenPropose={() => { setShowCommand(false); setTab("dnes"); setCreateWhat("suggest"); setCreateKey((k) => k + 1); }}
          onEdit={(ref) => { setShowCommand(false); setCmdPreload(""); setEditRef(ref); }} />}
        {editRef && <QuickEditModal refData={editRef} s={s} reload={reload} onClose={() => setEditRef(null)} />}
        {showContact && <ContactModal onClose={() => setShowContact(false)} />}
        {(tour || (!s.seen_tour && !tourClosed && !tutActive)) && (
          <GuidedTour lite={!!(s && s.lite_mode)} onTab={setTab} onClose={() => { setTour(false); setTourClosed(true); api("/api/profile/seen-tour", { method: "POST" }).then(reload).catch(() => {}); }} />
        )}
        {tutActive && (
          <OnboardTutorial s={s} reload={reload} goTo={tutGoTo}
            onFinish={(completed) => {
              setTutDismissed(true);
              if (completed) { setTab("prehlad"); }  // ukáž odznak v Prehľade
              api("/api/tutorial", { method: "POST", body: { done: true, completed, step: 99 } }).then(reload).catch(() => {});
            }} />
        )}
        {showTemplates && <TemplatesSheet onClose={() => setShowTemplates(false)} reload={reload} />}
        {showProfileMenu && <ProfileMenu me={me} refreshing={refreshing}
          onProfile={() => { setShowProfileMenu(false); setTab("profil"); }}
          onPublic={() => { setShowProfileMenu(false); openProfile(me.id); }}
          onRefresh={() => { setShowProfileMenu(false); refresh(); }}
          onLogout={() => { setShowProfileMenu(false); logout(); }}
          onClose={() => setShowProfileMenu(false)} />}
        {showNotifs && <NotifInbox items={s.notifications || []} onClose={() => { setShowNotifs(false); setNotifHighlight(null); }} highlightId={notifHighlight}
          onSeen={async () => { try { await api("/api/notifications/seen", { method: "POST" }); reload(); } catch (e) { /* */ } }}
          onDelete={async (id) => { try { await api("/api/notifications/" + id, { method: "DELETE" }); reload(); } catch (e) { /* */ } }}
          onClearAll={async () => { try { await api("/api/notifications", { method: "DELETE" }); reload(); } catch (e) { /* */ } }}
          onNavigate={navigateTo}
          onPoke={(n) => { setShowNotifs(false); setNotifHighlight(null); setPokeNotif(n); }} />}
        {/* v33 — rovnaký farebný level-up modal ako na Dnes, znova otvoriteľný ťuknutím na
            notifikáciu v zvončeku (kedykoľvek neskôr, nezávisle od aktuálneho tabu). */}
        {levelUpOpen && <LevelUpModal idx={levelUpOpen} onClose={() => setLevelUpOpen(null)} />}
        {pokeNotif && <PokeModal
          text={(() => { const b = pokeNotif.body || ""; const i = b.indexOf(": "); return i >= 0 ? b.slice(i + 2) : b; })()}
          fromName={pokeNotif.actor_name} fromId={pokeNotif.actor_id} canReply
          onClose={() => setPokeNotif(null)} onReplied={reload} />}
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
        {showChangelog && <ChangelogModal onClose={() => setShowChangelog(false)} />}
        {/* v32.21 — overlaye akcií globálneho profilu (Šťuchnúť / Výzva) */}
        {/* Item 1 — zIndex 340 > profil Sheet scrim (bz-scrim 300) aj > ChatView overlay (200),
            aby sa nudge sheet vykreslil NAD otvoreným verejným profilom (aj otvoreným z chatu). */}
        {profilePoke && <BuzerakSheet target={profilePoke} zIndex={340} onClose={() => setProfilePoke(null)} onSent={() => setProfilePoke(null)} />}
        {profileChal != null && <ChallengeCreate initialInvite={[profileChal]} open
          onOpenChange={(v) => { if (!v) setProfileChal(null); }} onAdded={reload} onDone={() => setProfileChal(null)} />}
        {/* T6 — potvrdenie odobratia z priateľov; po potvrdení DELETE /api/friends/<fid>,
            zavrieme profil (po znovuotvorení sa načíta stav „nie je priateľ") a refreshneme appku. */}
        {removeFriendAsk && (
          <Modal center title="Odobrať z priateľov?" icon="delete" maxWidth={400} onClose={() => setRemoveFriendAsk(null)}>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>
              Naozaj chceš odobrať <b>{removeFriendAsk.name}</b> z priateľov?
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <Button variant="ghost" block onClick={() => setRemoveFriendAsk(null)}>Zrušiť</Button>
              <Button variant="destructive" block onClick={async () => {
                const fid = removeFriendAsk.fid;
                setRemoveFriendAsk(null);
                closeProfile();
                try { await api("/api/friends/" + fid, { method: "DELETE" }); reload(); } catch (e) { /* */ }
              }}>Odobrať</Button>
            </div>
          </Modal>
        )}
      </div>
    </div>
    </ProfileModalProvider>
  );
}

function App() {
  // Public profile deep-link — render it regardless of auth (viewing is public).
  const profileMatch = window.location.pathname.match(/^\/u\/(\d+)$/);
  if (profileMatch) return <PublicProfile uid={parseInt(profileMatch[1], 10)} />;
  const [phase, setPhase] = useState("loading");
  const [user, setUser] = useState(null);
  const [allowReg, setAllowReg] = useState(true);
  const check = useCallback(async () => {
    const d = await api("/api/me");
    setSession({ ai: d.ai_enabled === true, premium: !!(d.user && d.user.premium), plan_until: d.user && d.user.plan_until, gender: d.user && d.user.gender, character: d.user && d.user.ai_character, characterName: d.user && d.user.ai_character_name, adminId: d.admin_id });  // v27 — plan_until: trial vidí „Predplatiť Community" (nie rozbité „spravovať"); admin_id → Avatar „admin" efekt
    if (d.user) { setUser(d.user); setPhase("app"); }
    else { setAllowReg(d.allow_registration !== false); setPhase("auth"); }
  }, []);
  useEffect(() => { check(); }, [check]);
  if (phase === "loading") return <div style={{ ...st.page, display: "flex", justifyContent: "center", padding: "80px 16px" }}><div className="spin" /></div>;
  // After login/logout reload "/" so the server serves the right landing (app vs homepage).
  if (phase === "auth") return <Auth allowReg={allowReg} onAuthed={() => { window.location.href = "/"; }} />;
  return <Main user={user} onLogout={() => { window.location.href = "/"; }} />;
}


export default App;
